import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccessControl } from "../context/useAccessControl";
import {
  buildPaymentOperationsStorageKey,
  getPaymentOperations,
  isPaymentOperationConfirmed,
  PAYMENT_PLATFORM_FEE_RATE,
  PAYMENT_OPERATIONS_UPDATED_EVENT,
  savePaymentOperations,
} from "../../services/local/paymentOperations";
import { saveOperationalOrdersState } from "../../services/local/operationalOrders";
import {
  buildCashRegisterSnapshot,
  buildCashRegisterStorageKey,
  CASH_REGISTER_UPDATED_EVENT,
  getCashRegisterState,
  saveCashRegisterState,
} from "../../services/local/cashRegister";
import {
  getBranchCommissionDebtSummary,
  getBranchCommissionCap,
  syncManualCommissionDebtForOperation,
} from "../../services/supabase/commissionLedgerService";
import { getMzBackofficeSnapshot } from "../../services/supabase/mzBillingService";
import {
  claimSharedOperationalOrderPayment,
  hydrateSharedOperationalStateToLocal,
  pushSharedCashRegisterState,
} from "../../services/supabase/operationalStateService";
import {
  checkMzCreditPackageCheckoutStatus,
  createMzCreditPackageCheckout,
  DEFAULT_MZ_CREDIT_PACKAGES,
  listEnabledMzCreditPackages,
} from "../../services/supabase/mzCreditPurchaseService";
import { refundApprovedDigitalPayment } from "../../services/supabase/kioskDigitalPaymentsService";

const PAYMENT_METHOD_LABELS = {
  counter: "Pagar en caja",
  pay_at_counter: "Pagar en caja",
  caja: "Pagar en caja",
  deep_link: "Deep link",
  pay_by_phone: "QR del kiosk",
  efectivo: "Efectivo",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
};

const STATUS_LABELS = {
  pending: "Pendiente",
  link_sent: "Link enviado",
  paid: "Cobrado",
  closed: "Cerrado",
  refunded: "Reintegrado",
};

const STATUS_STYLES = {
  pending: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  link_sent: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #93c5fd",
  },
  paid: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #86efac",
  },
  closed: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #cbd5e1",
  },
  refunded: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fca5a5",
  },
};

const SOURCE_LABELS = {
  app: "App del comensal",
  manual: "Carga manual",
};

const FILTER_OPTIONS = [
  { id: "pendientes", label: "Pendientes" },
  { id: "cobradas", label: "Cobradas" },
  { id: "cerradas", label: "Cerradas" },
  { id: "todas", label: "Todas" },
];

const CASH_MOVEMENT_KIND_LABELS = {
  income: "Ingreso",
  expense: "Egreso",
};

const CASH_MOVEMENT_REASON_PRESETS = {
  income: [
    "Cambio inicial",
    "Reposicion de caja",
    "Ajuste positivo",
    "Ingreso extraordinario",
  ],
  expense: [
    "Retiro de caja",
    "Gasto menor",
    "Pago a proveedor",
    "Ajuste negativo",
  ],
};

const MZ_KIOSK_UNITS_PER_1000 = 6;
const MZ_LOW_BALANCE_THRESHOLD_UNITS = 50000;
const MZ_DIGITAL_USAGE_TARGET = 65;
const MZ_DIGITAL_USAGE_REWARD_UNITS = 30000;

const MZ_VOLUME_REWARD_TIERS = [
  { minSalesAmount: 100000000, rewardUnits: 80000 },
  { minSalesAmount: 50000000, rewardUnits: 40000 },
  { minSalesAmount: 30000000, rewardUnits: 20000 },
  { minSalesAmount: 0, rewardUnits: 0 },
];

const waitForPromiseWithTimeout = (
  promise,
  timeoutMs = 25000,
  timeoutMessage = "La generacion del QR demoro demasiado. Reintenta en unos segundos."
) =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    Promise.resolve(promise)
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });

const initialManualForm = {
  mesa: "",
  canal: "Salon",
  amount: "",
  detail: "",
  method: "efectivo",
  notes: "",
};

const initialCashOpeningForm = {
  openingAmount: "",
  note: "",
};

const initialCashMovementForm = {
  kind: "expense",
  amount: "",
  reason: "",
  note: "",
};

const initialCashClosingForm = {
  countedAmount: "",
  note: "",
  confirmReviewed: false,
};

const createEmptyOperationalState = () => ({
  mesasActivas: [],
  pedidosHistoricos: [],
  salonTables: [],
  salonLayout: {},
  salonReservations: {},
  salonRooms: [],
  salonTableRooms: {},
  salonFixtures: {},
});

const createHistoryEntry = (label) => ({
  id: `history-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  label,
  at: new Date().toISOString(),
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatPercent = (value) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatUnits = (value) =>
  new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const hasMzCheckoutVisual = (checkout) =>
  Boolean(checkout?.qrData || checkout?.imageUrl || checkout?.checkoutUrl);

const formatDateTime = (value) => {
  if (!value) {
    return "Sin registrar";
  }

  return new Date(value).toLocaleString("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const escapeCsvCell = (value) =>
  `"${String(value ?? "")
    .replace(/"/g, '""')
    .replace(/\r?\n/g, " ")
    .trim()}"`;

const downloadCsvFile = (filename, content) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const blob = new Blob([`\uFEFF${content}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const buildCashRegisterExportCsv = (sessions, restaurantName) => {
  const headers = [
    "Sucursal",
    "Sesion",
    "Estado",
    "Apertura",
    "Abierta por",
    "Fondo inicial",
    "Efectivo cobrado",
    "Cobros contado",
    "Ingresos manuales",
    "Egresos manuales",
    "Esperado",
    "Contado real",
    "Diferencia",
    "Cierre",
    "Cerrada por",
    "Movimientos manuales",
    "Nota apertura",
    "Nota cierre",
  ];

  const rows = (Array.isArray(sessions) ? sessions : []).map((session) => [
    restaurantName || "Sucursal",
    session?.id || "",
    session?.status || "",
    formatDateTime(session?.openedAt),
    session?.openedBy || "Caja",
    Number(session?.openingAmount || 0).toFixed(2),
    Number(session?.cashSalesAmount || 0).toFixed(2),
    Number(session?.cashSalesCount || 0),
    Number(session?.manualIncomeAmount || 0).toFixed(2),
    Number(session?.manualExpenseAmount || 0).toFixed(2),
    Number(session?.expectedAmount || 0).toFixed(2),
    session?.countedClosingAmount === null || session?.countedClosingAmount === undefined
      ? ""
      : Number(session.countedClosingAmount || 0).toFixed(2),
    session?.differenceAmount === null || session?.differenceAmount === undefined
      ? ""
      : Number(session.differenceAmount || 0).toFixed(2),
    formatDateTime(session?.closedAt),
    session?.closedBy || "Caja",
    Array.isArray(session?.movements) ? session.movements.length : 0,
    session?.openingNote || "",
    session?.closingNote || "",
  ]);

  return [headers, ...rows]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");
};

const formatElapsed = (dateString) => {
  if (!dateString) {
    return "Sin tiempo";
  }

  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
};

const resolveOperatorLabel = (user) =>
  String(user?.email || user?.name || user?.id || "Equipo de caja").trim();

const isCashMethod = (method) => {
  const normalized = String(method || "")
    .trim()
    .toLowerCase();

  return normalized === "efectivo" || normalized === "cash";
};

const isCounterMethod = (method) => {
  const normalized = String(method || "")
    .trim()
    .toLowerCase();

  return (
    normalized === "counter" ||
    normalized === "pay_at_counter" ||
    normalized === "caja" ||
    normalized === "efectivo" ||
    normalized === "cash"
  );
};

const isCounterManagedFromInicioOperation = (operation) =>
  Boolean(
    operation?.source === "kiosk" &&
      isCounterMethod(operation?.selectedMethod || operation?.requestedMethod || "")
  );

const isKioskOperation = (operation) =>
  operation?.source === "kiosk" ||
  operation?.paymentRail === "kiosk_qr" ||
  operation?.selectedMethod === "pay_by_phone" ||
  operation?.requestedMethod === "pay_by_phone";

const isKioskDigitalOperation = (operation) =>
  operation?.selectedMethod === "pay_by_phone" ||
  operation?.requestedMethod === "pay_by_phone" ||
  operation?.paymentRail === "kiosk_qr";

const isSettledOperation = (operation) =>
  operation?.status === "paid" || operation?.status === "closed";

const getOperationReferenceDate = (operation) =>
  operation?.paidAt || operation?.closedAt || operation?.orderedAt || "";

const isDateInCurrentMonth = (dateString, now = new Date()) => {
  if (!dateString) {
    return false;
  }

  const targetDate = new Date(dateString);

  if (Number.isNaN(targetDate.getTime())) {
    return false;
  }

  return (
    targetDate.getFullYear() === now.getFullYear() &&
    targetDate.getMonth() === now.getMonth()
  );
};

const resolveMzVolumeRewardTier = (salesAmount) =>
  MZ_VOLUME_REWARD_TIERS.find(
    (tier) => salesAmount >= tier.minSalesAmount
  ) || MZ_VOLUME_REWARD_TIERS[MZ_VOLUME_REWARD_TIERS.length - 1];

const resolveNextMzVolumeTier = (salesAmount) =>
  [...MZ_VOLUME_REWARD_TIERS]
    .filter((tier) => tier.minSalesAmount > 0)
    .sort((left, right) => left.minSalesAmount - right.minSalesAmount)
    .find((tier) => salesAmount < tier.minSalesAmount) || null;

const sortOperations = (operations) =>
  [...operations].sort((a, b) => {
    const priority = {
      pending: 0,
      link_sent: 1,
      paid: 2,
      closed: 3,
    };

    const diff = (priority[a.status] ?? 99) - (priority[b.status] ?? 99);
    if (diff !== 0) {
      return diff;
    }

    return new Date(b.orderedAt).getTime() - new Date(a.orderedAt).getTime();
  });

const getGrossAmount = (operation) =>
  Number((operation?.grossAmount ?? operation?.amount) || 0);

const getEconomics = (operation) =>
  operation?.economics && typeof operation.economics === "object"
    ? operation.economics
    : null;

const getPlatformFeeAmount = (operation) =>
  Number(getEconomics(operation)?.platformFeeAmount || 0);

const getMerchantNetAmount = (operation) =>
  Number(getEconomics(operation)?.merchantNetAmount || 0);

const getRetainedAmount = (operation) =>
  Number(getEconomics(operation)?.retainedAmount || 0);

const getPendingDebtAmount = (operation) =>
  Number(getEconomics(operation)?.pendingDebtAmount || 0);

const getCommissionRate = (operation) => {
  const economicsRate = getEconomics(operation)?.platformFeeRate;
  const operationRate = operation?.commissionRate;
  const resolvedRate = operationRate ?? economicsRate ?? PAYMENT_PLATFORM_FEE_RATE;

  return Number(resolvedRate || 0);
};

const getPayoutAmount = (operation) => {
  const payoutValue = getEconomics(operation)?.payoutAmount;
  return payoutValue === null || payoutValue === undefined
    ? null
    : Number(payoutValue || 0);
};

function Pagos() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMzCreditsRoute = location.pathname === "/app/pagos/mz";
  const {
    canAccessModule,
    canPerformAction,
    currentBranch,
    currentAccessRoleLabel,
    currentCompany,
    currentRestaurant,
    user,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAccessControl();
  const activeBranchScope = currentBranch || currentRestaurant;
  const activeCompanyId =
    currentCompany?.id ||
    currentRestaurant?.companyId ||
    currentBranch?.companyId ||
    "";
  const activeBranchScopeId = activeBranchScope?.id || "default";
  const activeBranchScopeRef = useRef(activeBranchScope);
  activeBranchScopeRef.current = activeBranchScope;
  const [operations, setOperations] = useState(() =>
    sortOperations(getPaymentOperations(activeBranchScope))
  );
  const [cashRegisterState, setCashRegisterState] = useState(() =>
    getCashRegisterState(activeBranchScope)
  );
  const [selectedOperationId, setSelectedOperationId] = useState("");
  const [activeFilter, setActiveFilter] = useState("pendientes");
  const [searchValue, setSearchValue] = useState("");
  const [showManualForm, setShowManualForm] = useState(false);
  const [showMzCreditsPanel, setShowMzCreditsPanel] = useState(isMzCreditsRoute);
  const [mzPackages, setMzPackages] = useState(DEFAULT_MZ_CREDIT_PACKAGES);
  const [mzPackagesLoading, setMzPackagesLoading] = useState(false);
  const [mzPackagesError, setMzPackagesError] = useState("");
  const [selectedMzPackageCode, setSelectedMzPackageCode] = useState("");
  const [mzPackageCheckout, setMzPackageCheckout] = useState(null);
  const [mzPackageCheckoutError, setMzPackageCheckoutError] = useState("");
  const [mzPackageCheckoutPendingPack, setMzPackageCheckoutPendingPack] = useState(null);
  const [mzWalletSnapshot, setMzWalletSnapshot] = useState(null);
  const [mzWalletLoading, setMzWalletLoading] = useState(false);
  const [mzWalletError, setMzWalletError] = useState("");
  const mzCheckoutPanelRef = useRef(null);
  const [showCashMovementForm, setShowCashMovementForm] = useState(false);
  const [showCashClosingForm, setShowCashClosingForm] = useState(false);
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [cashOpeningForm, setCashOpeningForm] = useState(initialCashOpeningForm);
  const [cashMovementForm, setCashMovementForm] = useState(initialCashMovementForm);
  const [cashClosingForm, setCashClosingForm] = useState(initialCashClosingForm);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [ledgerDebtAmount, setLedgerDebtAmount] = useState(null);
  const [commissionCap, setCommissionCap] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setCommissionCap(null);
    const refresh = () => getBranchCommissionCap(activeBranchScopeId)
      .then((data) => { if (!cancelled) setCommissionCap(data); })
      .catch(() => { if (!cancelled) setCommissionCap(null); });
    refresh();
    const timer = window.setInterval(refresh, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [activeBranchScopeId, operations]);
  const canSeePaymentsModule = canAccessModule("pagos");
  // Permisos v1.1: Caja separa cobro, reversa y arqueo desde accessControl.
  const canChargeOrders =
    canSeePaymentsModule && canPerformAction("charge_orders");
  const canRefundOrders =
    canSeePaymentsModule && canPerformAction("refund_orders");
  const canManagePayments =
    canSeePaymentsModule && canPerformAction("manage_payments");
  const canOperateCashRegister = canManagePayments || canChargeOrders;

  useEffect(() => {
    const scopedBranch = activeBranchScopeRef.current;
    const nextOperations = sortOperations(getPaymentOperations(scopedBranch));
    setOperations(nextOperations);
    setCashRegisterState(getCashRegisterState(scopedBranch));
    setSelectedOperationId(nextOperations[0]?.id || "");
    setMzPackageCheckout(null);
    setMzPackageCheckoutError("");
    setMzPackageCheckoutPendingPack(null);
    setMzWalletSnapshot(null);
    setMzWalletLoading(false);
    setMzWalletError("");
    setShowCashMovementForm(false);
    setShowCashClosingForm(false);
    setCashOpeningForm(initialCashOpeningForm);
    setCashMovementForm(initialCashMovementForm);
    setCashClosingForm(initialCashClosingForm);
    setMessage({ type: "", text: "" });
  }, [activeBranchScopeId]);

  useEffect(() => {
    setShowMzCreditsPanel(isMzCreditsRoute);
  }, [activeBranchScopeId, isMzCreditsRoute]);

  useEffect(() => {
    if (!showMzCreditsPanel) {
      return undefined;
    }

    let isMounted = true;
    setMzPackagesLoading(true);
    setMzPackagesError("");

    listEnabledMzCreditPackages()
      .then((packages) => {
        if (!isMounted) {
          return;
        }

        setMzPackages(packages);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setMzPackages(DEFAULT_MZ_CREDIT_PACKAGES);
        setMzPackagesError(
          error?.message ||
            "No pudimos sincronizar los packs MZ. Mostramos los sugeridos."
        );
      })
      .finally(() => {
        if (isMounted) {
          setMzPackagesLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [showMzCreditsPanel]);

  useEffect(() => {
    if (isCreatorMode || !isSupabaseConfigured || !activeCompanyId) {
      setMzWalletSnapshot(null);
      setMzWalletLoading(false);
      setMzWalletError("");
      return undefined;
    }

    let cancelled = false;

    const loadMzWalletSnapshot = async () => {
      setMzWalletLoading(true);

      try {
        const snapshot = await getMzBackofficeSnapshot(activeCompanyId);

        if (cancelled) {
          return;
        }

        setMzWalletSnapshot(snapshot);
        setMzWalletError("");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setMzWalletSnapshot(null);
        setMzWalletError(
          error?.message || "No pudimos leer el saldo real de la billetera MZ."
        );
      } finally {
        if (!cancelled) {
          setMzWalletLoading(false);
        }
      }
    };

    void loadMzWalletSnapshot();

    return () => {
      cancelled = true;
    };
  }, [activeBranchScopeId, activeCompanyId, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    if (
      !mzCheckoutPanelRef.current ||
      (!selectedMzPackageCode &&
        !hasMzCheckoutVisual(mzPackageCheckout) &&
        !mzPackageCheckoutError)
    ) {
      return;
    }

    const nextFrameId = window.requestAnimationFrame(() => {
      mzCheckoutPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    return () => {
      window.cancelAnimationFrame(nextFrameId);
    };
  }, [mzPackageCheckout, mzPackageCheckoutError, selectedMzPackageCode]);

  useEffect(() => {
    if (
      !showMzCreditsPanel ||
      !mzPackageCheckout?.externalReference ||
      mzPackageCheckout?.status === "approved" ||
      mzPackageCheckoutError ||
      isCreatorMode ||
      !isSupabaseConfigured
    ) {
      return undefined;
    }

    const companyId = activeCompanyId;

    if (!companyId) {
      return undefined;
    }

    let cancelled = false;
    let closeTimeoutId = null;
    const scopedBranch = activeBranchScopeRef.current;

    const pollPaymentStatus = async () => {
      try {
        const result = await checkMzCreditPackageCheckoutStatus({
          companyId,
          branchId: mzPackageCheckout.branchId || currentBranch?.id || scopedBranch?.id || "",
          packageCode: mzPackageCheckout.packageCode,
          externalReference: mzPackageCheckout.externalReference,
          providerOrderId: mzPackageCheckout.providerOrderId,
          paymentId: mzPackageCheckout.providerPaymentHintId,
        });

        if (cancelled || !result?.approved) {
          return;
        }

        setMzPackageCheckout((prev) =>
          prev
            ? {
                ...prev,
                status: "approved",
              }
            : prev
        );
        setMzPackageCheckoutPendingPack(null);
        setMessage({
          type: "success",
          text: `Pago confirmado. Acreditamos ${formatUnits(
            result.unitsAmount || mzPackageCheckout.unitsAmount
          )} MZ en la billetera.`,
        });

        if (scopedBranch?.id) {
          try {
            await hydrateSharedOperationalStateToLocal(scopedBranch, () => ({
              mesasActivas: [],
              pedidosHistoricos: [],
            }));

            if (!cancelled) {
              setOperations(sortOperations(getPaymentOperations(scopedBranch)));
            }
          } catch (error) {
            console.error("No pudimos refrescar los pagos despues del cobro MZ:", error);
          }

          try {
            const summary = await getBranchCommissionDebtSummary(scopedBranch);

            if (!cancelled) {
              setLedgerDebtAmount(
                Number.isFinite(Number(summary?.outstandingDebtAmount))
                  ? Number(summary.outstandingDebtAmount)
                  : 0
              );
            }
          } catch (error) {
            console.error("No pudimos refrescar la deuda luego del cobro MZ:", error);
          }
        }

        try {
          const walletSnapshot = await getMzBackofficeSnapshot(companyId);

          if (!cancelled) {
            setMzWalletSnapshot(walletSnapshot);
            setMzWalletError("");
          }
        } catch (error) {
          console.error("No pudimos refrescar la billetera MZ despues del cobro:", error);

          if (!cancelled) {
            setMzWalletError(
              error?.message ||
                "No pudimos refrescar el saldo real de la billetera MZ."
            );
          }
        }

        closeTimeoutId = window.setTimeout(() => {
          if (cancelled) {
            return;
          }

          setMzPackageCheckout(null);
          setMzPackageCheckoutError("");
          setMzPackageCheckoutPendingPack(null);
        }, 2500);
      } catch (error) {
        if (!cancelled) {
          console.error("No pudimos revisar el estado del cobro MZ:", error);
        }
      }
    };

    const initialTimeoutId = window.setTimeout(() => {
      void pollPaymentStatus();
    }, 1500);
    const intervalId = window.setInterval(() => {
      void pollPaymentStatus();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeoutId);
      window.clearInterval(intervalId);

      if (closeTimeoutId) {
        window.clearTimeout(closeTimeoutId);
      }
    };
  }, [
    activeCompanyId,
    activeBranchScopeId,
    currentBranch,
    isCreatorMode,
    isSupabaseConfigured,
    mzPackageCheckout,
    mzPackageCheckoutError,
    showMzCreditsPanel,
  ]);

  useEffect(() => {
    const scopedBranch = activeBranchScopeRef.current;

    if (!scopedBranch?.id || isCreatorMode || !isSupabaseConfigured) {
      setLedgerDebtAmount(null);
      return undefined;
    }

    let cancelled = false;

    const syncRemoteState = async () => {
      try {
        await hydrateSharedOperationalStateToLocal(scopedBranch, () => ({
          mesasActivas: [],
          pedidosHistoricos: [],
        }));

        if (cancelled) {
          return;
        }

        setOperations(sortOperations(getPaymentOperations(scopedBranch)));
      } catch (error) {
        console.error("No pudimos hidratar los pagos remotos en Pagos:", error);
      }
    };

    const syncLedgerDebt = async () => {
      try {
        const summary = await getBranchCommissionDebtSummary(scopedBranch);

        if (cancelled) {
          return;
        }

        setLedgerDebtAmount(
          Number.isFinite(Number(summary?.outstandingDebtAmount))
            ? Number(summary.outstandingDebtAmount)
            : 0
        );
      } catch (error) {
        console.error("No pudimos leer la deuda acumulada de la sucursal:", error);
      }
    };

    void syncRemoteState();
    void syncLedgerDebt();

    return () => {
      cancelled = true;
    };
  }, [activeBranchScopeId, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    const restaurantId = activeBranchScopeId;
    const scopedBranch = activeBranchScopeRef.current;

    const syncOperations = () => {
      const nextOperations = sortOperations(getPaymentOperations(scopedBranch));
      setOperations(nextOperations);
    };

    const syncLedgerDebt = async () => {
      if (!scopedBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        setLedgerDebtAmount(null);
        return;
      }

      try {
        const summary = await getBranchCommissionDebtSummary(scopedBranch);
        setLedgerDebtAmount(
          Number.isFinite(Number(summary?.outstandingDebtAmount))
            ? Number(summary.outstandingDebtAmount)
            : 0
        );
      } catch (error) {
        console.error("No pudimos refrescar la deuda acumulada en Pagos:", error);
      }
    };

    const handlePaymentOperationsUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      const nextOperations = Array.isArray(event.detail?.operations)
        ? sortOperations(event.detail.operations)
        : sortOperations(getPaymentOperations(scopedBranch));

      setOperations(nextOperations);
      void syncLedgerDebt();
    };

    const handleStorage = (event) => {
      if (event.key !== buildPaymentOperationsStorageKey(scopedBranch?.id)) {
        return;
      }

      syncOperations();
      void syncLedgerDebt();
    };

    void syncLedgerDebt();

    window.addEventListener(
      PAYMENT_OPERATIONS_UPDATED_EVENT,
      handlePaymentOperationsUpdate
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        PAYMENT_OPERATIONS_UPDATED_EVENT,
        handlePaymentOperationsUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScopeId, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    const restaurantId = activeBranchScopeId;
    const scopedBranch = activeBranchScopeRef.current;

    const syncCashRegisterState = () => {
      setCashRegisterState(getCashRegisterState(scopedBranch));
    };

    const handleCashRegisterUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";

      if (updatedRestaurantId !== restaurantId) {
        return;
      }

      setCashRegisterState(
        event.detail?.state || getCashRegisterState(scopedBranch)
      );
    };

    const handleStorage = (event) => {
      if (event.key !== buildCashRegisterStorageKey(scopedBranch?.id)) {
        return;
      }

      syncCashRegisterState();
    };

    window.addEventListener(CASH_REGISTER_UPDATED_EVENT, handleCashRegisterUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        CASH_REGISTER_UPDATED_EVENT,
        handleCashRegisterUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScopeId]);

  const persistOperations = (updater) => {
    setOperations((prev) => {
      const nextValue = sortOperations(
        typeof updater === "function" ? updater(prev) : updater
      );
      savePaymentOperations(activeBranchScope, nextValue);
      return nextValue;
    });
  };

  // The payment claim was already persisted by Supabase. Only mirror it locally;
  // pushing it again could overwrite a simultaneous change from Mozo or Inicio.
  const applySharedPaymentClaim = (payload) => {
    if (!payload || typeof payload !== "object") {
      return [];
    }

    const savedOperations = savePaymentOperations(
      activeBranchScope,
      payload.paymentOperations || []
    );
    saveOperationalOrdersState(
      activeBranchScope,
      payload,
      createEmptyOperationalState
    );
    setOperations(sortOperations(savedOperations));
    return savedOperations;
  };

  const persistCashRegisterState = (updater) => {
    const currentState = getCashRegisterState(activeBranchScope);
    const nextValue =
      typeof updater === "function" ? updater(currentState) : updater;
    const savedState = saveCashRegisterState(activeBranchScope, nextValue);

    setCashRegisterState(savedState);

    if (
      activeBranchScope?.id &&
      isSupabaseConfigured &&
      !isCreatorMode
    ) {
      pushSharedCashRegisterState(activeBranchScope, savedState).catch((error) => {
        console.error("No pudimos sincronizar la caja con Supabase:", error);
      });
    }

    return savedState;
  };

  const syncManualCommissionDebtSnapshot = async (operation, note = "") => {
    if (
      !operation?.id ||
      !activeBranchScope?.id ||
      isCreatorMode ||
      !isSupabaseConfigured
    ) {
      return null;
    }

    try {
      return await syncManualCommissionDebtForOperation({
        branch: activeBranchScope,
        operation,
        note,
        metadata: {
          uiSource: "pagos",
        },
      });
    } catch (error) {
      console.error(
        "No pudimos sincronizar la deuda manual de comision desde Pagos:",
        error
      );
      return null;
    }
  };

  const filteredOperations = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return operations.filter((operation) => {
      if (isCounterManagedFromInicioOperation(operation)) {
        return false;
      }

      const matchesFilter =
        activeFilter === "todas"
          ? true
          : activeFilter === "pendientes"
            ? operation.status === "pending" || operation.status === "link_sent"
            : activeFilter === "cobradas"
              ? operation.status === "paid"
              : operation.status === "closed";

      if (!matchesFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        operation.mesa,
        operation.canal,
        operation.itemsSummary,
        operation.notes,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [activeFilter, operations, searchValue]);

  useEffect(() => {
    if (!filteredOperations.length) {
      setSelectedOperationId("");
      return;
    }

    const stillExists = filteredOperations.some(
      (operation) => operation.id === selectedOperationId
    );

    if (!stillExists) {
      setSelectedOperationId(filteredOperations[0].id);
    }
  }, [filteredOperations, selectedOperationId]);

  const selectedOperation =
    filteredOperations.find((operation) => operation.id === selectedOperationId) ||
    operations.find(
      (operation) =>
        operation.id === selectedOperationId &&
        !isCounterManagedFromInicioOperation(operation)
    ) ||
    filteredOperations[0] ||
    null;
  const selectedOperationManagedFromInicio = Boolean(
    isCounterManagedFromInicioOperation(selectedOperation)
  );
  const selectedOperationAlreadyClaimed = Boolean(
    selectedOperation?.orderId &&
      operations.some(
        (operation) =>
          operation.id !== selectedOperation.id &&
          operation.orderId === selectedOperation.orderId &&
          isPaymentOperationConfirmed(operation)
      )
  );

  const cashSnapshot = useMemo(
    () => buildCashRegisterSnapshot(cashRegisterState, operations, new Date()),
    [cashRegisterState, operations]
  );
  const activeCashSession = cashSnapshot.activeSession;
  const uncoveredCashCount = cashSnapshot.uncoveredCashOperations.length;
  const cashOperatorLabel = useMemo(() => resolveOperatorLabel(user), [user]);
  const cashMovementReasonPresets = useMemo(
    () => CASH_MOVEMENT_REASON_PRESETS[cashMovementForm.kind] || [],
    [cashMovementForm.kind]
  );
  const closingDifferencePreview = useMemo(() => {
    if (!activeCashSession) {
      return null;
    }

    if (cashClosingForm.countedAmount === "") {
      return null;
    }

    return (
      Number(cashClosingForm.countedAmount || 0) -
      Number(activeCashSession.expectedAmount || 0)
    );
  }, [activeCashSession, cashClosingForm.countedAmount]);
  const pendingCashOperationsForClosing = useMemo(
    () =>
      operations.filter((operation) => {
        const status = String(operation?.status || "")
          .trim()
          .toLowerCase();

        return (
          isCashMethod(operation?.selectedMethod || operation?.requestedMethod) &&
          (status === "pending" || status === "link_sent")
        );
      }),
    [operations]
  );
  const closedCashSessions = useMemo(
    () =>
      (cashSnapshot.sessions || [])
        .filter((session) => session.status === "closed")
        .sort(
          (left, right) =>
            new Date(right.closedAt || right.updatedAt || 0).getTime() -
            new Date(left.closedAt || left.updatedAt || 0).getTime()
        ),
    [cashSnapshot.sessions]
  );

  const metrics = useMemo(() => {
    const visibleOperations = operations.filter(
      (operation) => !isCounterManagedFromInicioOperation(operation)
    );
    const pendingOperations = visibleOperations.filter(
      (operation) => operation.status === "pending" || operation.status === "link_sent"
    );
    const paidOperations = visibleOperations.filter(
      (operation) => operation.status === "paid" || operation.status === "closed"
    );
    const readyToClose = visibleOperations.filter(
      (operation) => operation.status === "paid"
    );
    const linkSentCount = visibleOperations.filter(
      (operation) => operation.status === "link_sent"
    ).length;
    const pendingDebtAmount =
      ledgerDebtAmount === null
        ? paidOperations.reduce(
            (sum, operation) => sum + getPendingDebtAmount(operation),
            0
          )
        : ledgerDebtAmount;
    const platformFeesAmount = paidOperations.reduce(
      (sum, operation) => sum + getPlatformFeeAmount(operation),
      0
    );
    const merchantNetAmount = paidOperations.reduce(
      (sum, operation) => sum + getMerchantNetAmount(operation),
      0
    );

    return [
      {
        id: "pending-amount",
        label: "Por cobrar",
        value: formatCurrency(
          pendingOperations.reduce((sum, operation) => sum + getGrossAmount(operation), 0)
        ),
        detail: `${pendingOperations.length} operaciones pendientes`,
      },
      {
        id: "paid-amount",
        label: "Cobrado bruto",
        value: formatCurrency(
          paidOperations.reduce((sum, operation) => sum + getGrossAmount(operation), 0)
        ),
        detail: `${paidOperations.length} operaciones ya registradas`,
      },
      {
        id: "merchant-net",
        label: "Neto del local",
        value: formatCurrency(merchantNetAmount),
        detail: "Venta despues de la comision base",
      },
      {
        id: "platform-fees",
        label: "Comision retenida",
        value: formatCurrency(platformFeesAmount),
        detail: "Incluye comision propia y deudas compensadas",
      },
      {
        id: "debt-pending",
        label: "Deuda a compensar",
        value: formatCurrency(pendingDebtAmount),
        detail: "Se descuenta del proximo cobro digital",
      },
      {
        id: "ready-close",
        label: "Listas para cerrar",
        value: String(readyToClose.length),
        detail: `${linkSentCount} deep link${linkSentCount === 1 ? "" : "s"} en espera`,
      },
    ];
  }, [ledgerDebtAmount, operations]);

  const mzKioskSnapshot = useMemo(() => {
    const now = new Date();
    const monthKioskOperations = operations.filter(
      (operation) =>
        isKioskOperation(operation) &&
        isSettledOperation(operation) &&
        isDateInCurrentMonth(getOperationReferenceDate(operation), now)
    );
    const kioskSalesAmount = monthKioskOperations.reduce(
      (sum, operation) => sum + getGrossAmount(operation),
      0
    );
    const digitalOperationsCount = monthKioskOperations.filter(
      isKioskDigitalOperation
    ).length;
    const totalOperationsCount = monthKioskOperations.length;
    const digitalUsagePercent =
      totalOperationsCount > 0
        ? (digitalOperationsCount / totalOperationsCount) * 100
        : 0;
    const rewardTier = resolveMzVolumeRewardTier(kioskSalesAmount);
    const digitalRewardUnits =
      digitalUsagePercent >= MZ_DIGITAL_USAGE_TARGET
        ? MZ_DIGITAL_USAGE_REWARD_UNITS
        : 0;
    const volumeRewardUnits = rewardTier.rewardUnits;
    const expectedRewardUnits = digitalRewardUnits + volumeRewardUnits;
    const nextVolumeTier = resolveNextMzVolumeTier(kioskSalesAmount);
    const digitalOrdersMissing =
      totalOperationsCount > 0
        ? Math.max(
            0,
            Math.ceil((MZ_DIGITAL_USAGE_TARGET / 100) * totalOperationsCount) -
              digitalOperationsCount
          )
        : 0;

    return {
      monthKioskOperations,
      kioskSalesAmount,
      digitalOperationsCount,
      totalOperationsCount,
      digitalUsagePercent,
      digitalRewardUnits,
      volumeRewardUnits,
      expectedRewardUnits,
      nextVolumeTier,
      digitalOrdersMissing,
    };
  }, [operations]);

  const mzRealBalanceUnits = Number.isFinite(
    Number(mzWalletSnapshot?.companyWallet?.balanceUnits)
  )
    ? Number(mzWalletSnapshot.companyWallet.balanceUnits)
    : null;
  const mzDisplayBalanceUnits = mzRealBalanceUnits;

  const registerMessage = (type, text) => {
    setMessage({ type, text });
  };

  const handleMzPackageAction = async (pack) => {
    if (!canManagePayments) {
      registerMessage(
        "error",
        `${currentAccessRoleLabel} puede revisar Creditos MZ, pero la compra la confirma un administrador.`
      );
      return;
    }

    if (isCreatorMode || !isSupabaseConfigured) {
      registerMessage(
        "error",
        "Para comprar MZ hay que estar conectado a Supabase con una empresa real."
      );
      return;
    }

    const companyId = activeCompanyId;
    const branchId = currentBranch?.id || activeBranchScope?.id || "";

    if (!companyId) {
      registerMessage(
        "error",
        "No encontramos la empresa activa para iniciar la compra de MZ."
      );
      return;
    }

    const packageCode = pack.packageCode || pack.id;
    setMzPackageCheckout(null);
    setMzPackageCheckoutError("");
    setMzPackageCheckoutPendingPack(pack);
    setSelectedMzPackageCode(packageCode);
    registerMessage(
      "success",
      `Generando QR interoperable de Mercado Pago para ${pack.name}...`
    );

    try {
      const checkout = await waitForPromiseWithTimeout(
        createMzCreditPackageCheckout({
          companyId,
          branchId,
          packageCode,
          returnUrl: typeof window !== "undefined" ? window.location.href : "",
        })
      );

      setMzPackageCheckout(checkout);
      setMzPackageCheckoutError("");

      registerMessage(
        "success",
        `QR interoperable generado para comprar ${formatUnits(
          checkout.unitsAmount || pack.units
        )} MZ. Cuando Mercado Pago confirme el cobro, se acredita en la billetera.`
      );
    } catch (error) {
      setMzPackageCheckout(null);
      setMzPackageCheckoutError(
        error?.message || "No pudimos iniciar la compra del pack MZ."
      );
      registerMessage(
        "error",
        error?.message || "No pudimos iniciar la compra del pack MZ."
      );
    } finally {
      setSelectedMzPackageCode("");
    }
  };

  const ensurePaymentPermission = (allowed, actionLabel) => {
    if (allowed) {
      return true;
    }

    registerMessage(
      "error",
      `${currentAccessRoleLabel} no puede ${actionLabel} desde Caja.`
    );
    return false;
  };

  const exportCashRegisterHistory = () => {
    if (!ensurePaymentPermission(canManagePayments, "exportar arqueos")) {
      return;
    }

    if (closedCashSessions.length === 0) {
      registerMessage(
        "error",
        "Todavia no hay arqueos cerrados para exportar en esta sucursal."
      );
      return;
    }

    const safeBranchName = String(
      currentBranch?.name || activeBranchScope?.name || "sucursal"
    )
      .trim()
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase();
    const csv = buildCashRegisterExportCsv(
      closedCashSessions,
      currentBranch?.name || activeBranchScope?.name || "Sucursal"
    );

    downloadCsvFile(
      `arqueos-${safeBranchName || "sucursal"}-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      csv
    );

    registerMessage("success", "Exportamos la bitacora de arqueos en CSV.");
  };

  const openCashSession = (event) => {
    event.preventDefault();

    if (!ensurePaymentPermission(canOperateCashRegister, "abrir caja")) {
      return;
    }

    if (activeCashSession) {
      registerMessage("error", "Ya hay una caja abierta para esta sucursal.");
      return;
    }

    if (cashOpeningForm.openingAmount === "" || Number(cashOpeningForm.openingAmount) < 0) {
      registerMessage("error", "Carga un fondo inicial valido para abrir caja.");
      return;
    }

    persistCashRegisterState((prev) => ({
      ...prev,
      sessions: [
        {
          id: `cash-session-${Date.now()}`,
          status: "open",
          openedAt: new Date().toISOString(),
          openedBy: cashOperatorLabel,
          openingAmount: Number(cashOpeningForm.openingAmount || 0),
          openingNote: cashOpeningForm.note.trim(),
          movements: [],
        },
        ...(prev?.sessions || []),
      ],
    }));

    setCashOpeningForm(initialCashOpeningForm);
    registerMessage("success", "Caja abierta correctamente.");
  };

  const addCashMovement = (event) => {
    event.preventDefault();

    if (!ensurePaymentPermission(canOperateCashRegister, "registrar movimientos de caja")) {
      return;
    }

    if (!activeCashSession) {
      registerMessage("error", "Primero abre caja antes de registrar movimientos.");
      return;
    }

    if (!cashMovementForm.amount || Number(cashMovementForm.amount) <= 0) {
      registerMessage("error", "Carga un importe valido para el movimiento de caja.");
      return;
    }

    if (!cashMovementForm.reason.trim()) {
      registerMessage("error", "Define el motivo del ingreso o egreso antes de guardarlo.");
      return;
    }

    persistCashRegisterState((prev) => ({
      ...prev,
      sessions: (prev?.sessions || []).map((session) =>
        session.id === activeCashSession.id
          ? {
              ...session,
              movements: [
                ...(session.movements || []),
                {
                  id: `cash-movement-${Date.now()}`,
                  kind: cashMovementForm.kind,
                  amount: Number(cashMovementForm.amount || 0),
                  reason: cashMovementForm.reason.trim(),
                  note: cashMovementForm.note.trim(),
                  createdBy: cashOperatorLabel,
                  createdAt: new Date().toISOString(),
                },
              ],
            }
          : session
      ),
    }));

    setCashMovementForm(initialCashMovementForm);
    setShowCashMovementForm(false);
    registerMessage(
      "success",
      `${CASH_MOVEMENT_KIND_LABELS[cashMovementForm.kind]} registrada en caja.`
    );
  };

  const closeCashSession = (event) => {
    event.preventDefault();

    if (!ensurePaymentPermission(canOperateCashRegister, "cerrar caja")) {
      return;
    }

    if (!activeCashSession) {
      registerMessage("error", "No hay una caja abierta para cerrar.");
      return;
    }

    if (cashClosingForm.countedAmount === "" || Number(cashClosingForm.countedAmount) < 0) {
      registerMessage("error", "Carga el contado real antes de cerrar la caja.");
      return;
    }

    if (pendingCashOperationsForClosing.length > 0) {
      registerMessage(
        "error",
        `Todavia hay ${pendingCashOperationsForClosing.length} cobro${pendingCashOperationsForClosing.length === 1 ? "" : "s"} en efectivo pendiente${pendingCashOperationsForClosing.length === 1 ? "" : "s"}. Cierra o cobra esas operaciones antes de cerrar la caja.`
      );
      return;
    }

    if (!cashClosingForm.confirmReviewed) {
      registerMessage(
        "error",
        "Marca la confirmacion del arqueo antes de cerrar la caja."
      );
      return;
    }

    const difference = Number(cashClosingForm.countedAmount || 0) -
      Number(activeCashSession.expectedAmount || 0);

    persistCashRegisterState((prev) => ({
      ...prev,
      sessions: (prev?.sessions || []).map((session) =>
        session.id === activeCashSession.id
          ? {
              ...session,
              status: "closed",
              closedAt: new Date().toISOString(),
              closedBy: cashOperatorLabel,
              countedClosingAmount: Number(cashClosingForm.countedAmount || 0),
              closingNote: cashClosingForm.note.trim(),
            }
          : session
      ),
    }));

    setCashClosingForm(initialCashClosingForm);
    setShowCashClosingForm(false);
    registerMessage(
      "success",
      difference === 0
        ? "Caja cerrada sin diferencias."
        : difference > 0
          ? `Caja cerrada con sobrante de ${formatCurrency(difference)}.`
          : `Caja cerrada con faltante de ${formatCurrency(Math.abs(difference))}.`
    );
  };

  const updateOperation = async (
    operationId,
    updater,
    successText,
    afterUpdate = null
  ) => {
    let updatedOperation = null;

    persistOperations((prev) =>
      prev.map((operation) => {
        if (operation.id !== operationId) {
          return operation;
        }

        updatedOperation = updater(operation);
        return updatedOperation;
      })
    );

    if (successText) {
      registerMessage("success", successText);
    }

    if (typeof afterUpdate === "function" && updatedOperation) {
      await afterUpdate(updatedOperation);
    }
  };

  const setOperationMethod = async (operationId, method) => {
    if (!ensurePaymentPermission(canChargeOrders, "cambiar el medio final de cobro")) {
      return;
    }

    await updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        selectedMethod: method,
      }),
      `Medio final actualizado a ${PAYMENT_METHOD_LABELS[method]}.`,
      (operation) =>
        syncManualCommissionDebtSnapshot(
          operation,
          "Medio final ajustado desde Caja."
        )
    );
  };

  const updateOperationNotes = (operationId, notes) => {
    if (!ensurePaymentPermission(canManagePayments, "editar notas internas")) {
      return;
    }

    persistOperations((prev) =>
      prev.map((operation) =>
        operation.id === operationId ? { ...operation, notes } : operation
      )
    );
  };

  const sendDeepLink = (operationId) => {
    if (!ensurePaymentPermission(canChargeOrders, "enviar deep links")) {
      return;
    }

    const targetOperation = operations.find((operation) => operation.id === operationId);
    const alreadyClaimed = Boolean(
      targetOperation?.orderId &&
        operations.some(
          (operation) =>
            operation.id !== targetOperation.id &&
            operation.orderId === targetOperation.orderId &&
            isPaymentOperationConfirmed(operation)
        )
    );

    if (alreadyClaimed) {
      registerMessage(
        "error",
        "Este pedido ya fue cobrado desde Caja o por un mozo. No enviaremos otro link de pago."
      );
      return;
    }

    void updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        status: "link_sent",
        history: [
          createHistoryEntry("Deep link enviado al comensal."),
          ...(operation.history || []),
        ],
      }),
      "Deep link enviado. Queda a la espera de confirmacion."
    );
  };

  const markAsPaid = async (operationId) => {
    if (!ensurePaymentPermission(canChargeOrders, "registrar cobros")) {
      return;
    }

    const targetOperation = operations.find((operation) => operation.id === operationId);

    if (!targetOperation) {
      return;
    }

    if (isPaymentOperationConfirmed(targetOperation)) {
      registerMessage(
        "error",
        "Esta operacion ya tiene un cobro confirmado y no puede registrarse dos veces."
      );
      return;
    }

    if (isCashMethod(targetOperation.selectedMethod) && !activeCashSession) {
      registerMessage(
        "error",
        "Primero abre caja antes de registrar un cobro en efectivo."
      );
      return;
    }

    const methodLabel = PAYMENT_METHOD_LABELS[targetOperation.selectedMethod] || "medio elegido";
    const paidAt = new Date().toISOString();
    const paidOperation = {
      ...targetOperation,
      status: "paid",
      paidAt,
      updatedAt: paidAt,
      history: [
        createHistoryEntry(`Cobro registrado por ${methodLabel}.`),
        ...(targetOperation.history || []),
      ],
    };

    if (
      targetOperation.orderId &&
      activeBranchScope?.id &&
      isSupabaseConfigured &&
      !isCreatorMode
    ) {
      try {
        const paymentClaim = await claimSharedOperationalOrderPayment({
          restaurant: activeBranchScope,
          orderId: targetOperation.orderId,
          paymentOperation: paidOperation,
          orderPatch: {
            paymentStatus: "paid",
            paymentMethod: targetOperation.selectedMethod || "counter",
            selectedPaymentMethod: targetOperation.selectedMethod || "counter",
            requestedPaymentMethod:
              targetOperation.requestedMethod || targetOperation.selectedMethod || "counter",
            cashPaymentPending: false,
            paymentOperationId: targetOperation.id,
            paidAt,
            paymentClaimSource: "caja",
            paymentClaimedBy: user?.id || "",
          },
          fallbackFactory: createEmptyOperationalState,
        });

        if (paymentClaim.supported) {
          applySharedPaymentClaim(paymentClaim.payload);

          if (!paymentClaim.claimed) {
            registerMessage(
              "error",
              paymentClaim.reason === "payment_in_progress"
                ? "Hay un cobro digital pendiente para este pedido. Espera su confirmacion o cancelalo antes de cobrar otra vez."
                : "Este pedido ya fue cobrado desde Caja o por un mozo."
            );
            return;
          }

          registerMessage("success", `Cobro registrado en ${methodLabel}.`);
          await syncManualCommissionDebtSnapshot(
            paidOperation,
            `Cobro registrado en ${methodLabel} desde Caja.`
          );
          return;
        }
      } catch (error) {
        registerMessage(
          "error",
          error?.message ||
            "No pudimos confirmar el cobro compartido. El pedido sigue pendiente para evitar duplicados."
        );
        return;
      }
    }

    await updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        status: "paid",
        paidAt,
        updatedAt: paidAt,
        history: paidOperation.history,
      }),
      `Cobro registrado en ${methodLabel}.`,
      (operation) =>
        syncManualCommissionDebtSnapshot(
          operation,
          `Cobro registrado en ${methodLabel} desde Caja.`
        )
    );
  };

  const revertToPending = async (operationId) => {
    if (!ensurePaymentPermission(canRefundOrders, "revertir cobros")) {
      return;
    }

    const targetOperation = operations.find(
      (operation) => operation.id === operationId
    );

    if (!targetOperation) {
      return;
    }

    if (targetOperation.status === "paid" && targetOperation.paymentIntentId) {
      const reason = window.prompt(
        `Motivo del reintegro de ${targetOperation.mesa || "la operación"}:`,
        "Pedido cancelado por el restaurante"
      );
      if (reason === null) return;
      if (String(reason).trim().length < 5) {
        registerMessage("error", "Indica brevemente el motivo del reintegro.");
        return;
      }
      if (
        !window.confirm(
          `Se devolverá el total de ${formatCurrency(targetOperation.amount)} al cliente por Mercado Pago. La comisión de MozzoApp se conserva: la parte devuelta por Mercado Pago quedará como deuda del restaurante, sin duplicar lo ya registrado. Si este pago había cancelado deuda anterior, esa deuda se restablecerá. ¿Continuar?`
        )
      ) {
        return;
      }

      try {
        await refundApprovedDigitalPayment({
          branchId: activeBranchScope?.id,
          intentId: targetOperation.paymentIntentId,
          orderId: targetOperation.orderId || "",
          reason,
        });

        await updateOperation(
          operationId,
          (operation) => ({
            ...operation,
            status: "refunded",
            refundedAt: new Date().toISOString(),
            refundReason: String(reason).trim(),
            closedAt: null,
            history: [
              createHistoryEntry("Pago reintegrado al cliente por Mercado Pago."),
              ...(operation.history || []),
            ],
          }),
          "El pago fue reintegrado al cliente. La comisión se conserva y el saldo pendiente del restaurante fue actualizado."
        );
      } catch (refundError) {
        registerMessage(
          "error",
          refundError?.message ||
            "No pudimos confirmar el reintegro. El cobro sigue registrado."
        );
      }
      return;
    }

    await updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        status: "pending",
        paidAt: null,
        closedAt: null,
        history: [
          createHistoryEntry("Operacion vuelta a pendiente para revisar el cobro."),
          ...(operation.history || []),
        ],
      }),
      "La operacion volvio a pendiente.",
      (operation) =>
        syncManualCommissionDebtSnapshot(
          operation,
          "Operacion devuelta a pendiente desde Caja."
        )
    );
  };

  const closeOperation = async (operationId) => {
    if (!ensurePaymentPermission(canChargeOrders, "cerrar operaciones")) {
      return;
    }

    await updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        status: "closed",
        closedAt: new Date().toISOString(),
        history: [
          createHistoryEntry("Operacion cerrada desde caja."),
          ...(operation.history || []),
        ],
      }),
      "Operacion cerrada correctamente.",
      (operation) =>
        syncManualCommissionDebtSnapshot(
          operation,
          "Operacion cerrada desde Caja."
        )
    );
  };

  const reopenOperation = async (operationId) => {
    if (!ensurePaymentPermission(canRefundOrders, "reabrir operaciones")) {
      return;
    }

    await updateOperation(
      operationId,
      (operation) => ({
        ...operation,
        status: operation.paidAt ? "paid" : "pending",
        closedAt: null,
        history: [
          createHistoryEntry("Operacion reabierta para continuar la gestion."),
          ...(operation.history || []),
        ],
      }),
      "Operacion reabierta.",
      (operation) =>
        syncManualCommissionDebtSnapshot(
          operation,
          "Operacion reabierta desde Caja."
        )
    );
  };

  const handleCreateManualOperation = (event) => {
    event.preventDefault();

    if (!ensurePaymentPermission(canChargeOrders, "crear operaciones manuales")) {
      return;
    }

    if (!manualForm.mesa.trim()) {
      registerMessage("error", "La mesa o referencia de la operacion es obligatoria.");
      return;
    }

    if (!manualForm.amount || Number(manualForm.amount) <= 0) {
      registerMessage("error", "Carga un monto valido para la operacion manual.");
      return;
    }

    const now = new Date().toISOString();
    const methodLabel = PAYMENT_METHOD_LABELS[manualForm.method];
    const newOperation = {
      id: `manual-${Date.now()}`,
      mesa: manualForm.mesa.trim(),
      canal: manualForm.canal,
      source: "manual",
      requestedMethod: null,
      selectedMethod: manualForm.method,
      status: "pending",
      amount: Number(manualForm.amount),
      grossAmount: Number(manualForm.amount),
      orderedAt: now,
      paidAt: null,
      closedAt: null,
      itemsSummary: manualForm.detail.trim() || "Operacion manual sin detalle cargado.",
      notes: manualForm.notes.trim(),
      history: [
        createHistoryEntry("Operacion manual creada desde la pantalla de caja."),
        createHistoryEntry(`Medio propuesto para cobrar: ${methodLabel}.`),
      ],
    };

    persistOperations((prev) => [newOperation, ...prev]);
    setSelectedOperationId(newOperation.id);
    setManualForm(initialManualForm);
    setShowManualForm(false);
    registerMessage("success", "Operacion manual creada y lista para cobrar.");
  };

  const mzCreditsButtonLabel =
    commissionCap?.usesMz === false || mzDisplayBalanceUnits === null
      ? "Creditos MZ"
      : mzDisplayBalanceUnits <= 0
      ? "Regularizar MZ"
      : mzDisplayBalanceUnits <= MZ_LOW_BALANCE_THRESHOLD_UNITS
        ? "Saldo bajo MZ"
        : "Creditos MZ";
  const mzCreditsButtonTone =
    commissionCap?.usesMz === false || mzDisplayBalanceUnits === null
      ? {}
      : mzDisplayBalanceUnits <= 0
      ? styles.mzButtonDanger
      : mzDisplayBalanceUnits <= MZ_LOW_BALANCE_THRESHOLD_UNITS
        ? styles.mzButtonWarning
        : {};

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.headerCopy}>
          <div style={styles.overline}>
            {isMzCreditsRoute ? "Caja · Billetera MZ" : "Caja"}
          </div>
          <h1 style={styles.title}>
            {isMzCreditsRoute ? "Comprar creditos MZ" : "Cierre de operaciones"}
          </h1>
          <p style={styles.subtitle}>
            {isMzCreditsRoute
              ? "Revisa el saldo compartido por la empresa, elige un pack y genera el QR interoperable para acreditarlo automaticamente cuando se confirme el pago."
              : "Desde aqui sigues los cobros del turno, revisas lo que ya se liquido y mantienes visible la comision por sucursal. Los pedidos del totem que se pagan en caja se resuelven desde Inicio > Notificaciones y aqui solo quedan como seguimiento."}
          </p>
          <div style={styles.branchPill}>
            Sucursal activa: {currentBranch?.name || activeBranchScope?.name || "Sucursal en uso"}
          </div>
        </div>

        <div style={styles.headerActions}>
          <button
            type="button"
            style={{
              ...styles.mzHeaderButton,
              ...mzCreditsButtonTone,
              ...(showMzCreditsPanel ? styles.mzHeaderButtonActive : {}),
            }}
            onClick={() =>
              navigate(isMzCreditsRoute ? "/app/pagos" : "/app/pagos/mz")
            }
          >
            <span>{isMzCreditsRoute ? "Volver a Caja" : mzCreditsButtonLabel}</span>
            <strong>{mzDisplayBalanceUnits === null ? "Sin datos" : `${formatUnits(mzDisplayBalanceUnits)} MZ`}</strong>
          </button>
          {!isMzCreditsRoute ? (
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => setShowManualForm((prev) => !prev)}
              disabled={!canChargeOrders}
            >
              {showManualForm ? "Ocultar carga manual" : "Registrar operacion manual"}
            </button>
          ) : null}
        </div>
      </section>

      {!canManagePayments ? (
        <div style={{ ...styles.notice, ...styles.noticeWarning }}>
          Modo restringido: {currentAccessRoleLabel} puede ver la caja, pero no abrir ni cerrar
          arqueos sin permiso operativo.
        </div>
      ) : null}

      {message.text && (
        <div
          style={{
            ...styles.notice,
            ...(message.type === "error" ? styles.noticeError : styles.noticeSuccess),
          }}
        >
          {message.text}
        </div>
      )}

      {showMzCreditsPanel && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Creditos MZ</h2>
              <p style={styles.panelText}>
                Los MZ respaldan las operaciones manuales y en efectivo cuando la empresa
                no utiliza el cobro integrado. El saldo es compartido entre sus sucursales.
              </p>
            </div>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate("/app/pagos")}
            >
              Volver a Caja
            </button>
          </div>

          <div style={styles.mzSummaryGrid}>
            <article style={styles.mzSummaryCard}>
              <span style={styles.metricLabel}>
                {mzRealBalanceUnits === null ? "Saldo MZ pendiente" : "Saldo MZ real"}
              </span>
              <strong style={styles.metricValue}>
                {mzDisplayBalanceUnits === null ? "Sin datos" : `${formatUnits(mzDisplayBalanceUnits)} MZ`}
              </strong>
              <span style={styles.metricDetail}>
                {mzWalletLoading
                  ? "Sincronizando saldo real de la billetera MZ..."
                  : mzRealBalanceUnits === null
                  ? "No pudimos consultar el saldo. No se estima a partir de las comisiones."
                  : "Saldo real de la billetera MZ a nivel empresa, compartido entre sucursales."}
              </span>
            </article>

            <article style={styles.mzSummaryCard}>
              <span style={styles.metricLabel}>Ventas kiosk del mes</span>
              <strong style={styles.metricValue}>
                {formatCurrency(mzKioskSnapshot.kioskSalesAmount)}
              </strong>
              <span style={styles.metricDetail}>
                {mzKioskSnapshot.totalOperationsCount} operacion
                {mzKioskSnapshot.totalOperationsCount === 1 ? "" : "es"} kiosk cobradas.
              </span>
            </article>

            <article style={styles.mzSummaryCard}>
              <span style={styles.metricLabel}>Tope de comisiones por sucursal</span>
              <strong style={styles.metricValue}>
                {commissionCap ? formatCurrency(commissionCap.capAmount) : "Sin confirmar"}
              </strong>
              <span style={styles.metricDetail}>
                {commissionCap
                  ? `Disponible este mes: ${formatCurrency(commissionCap.remainingAmount)}. Incluye reservas de cobros pendientes.`
                  : "No se pudo consultar el tope vigente en el servidor."}
              </span>
            </article>

            <article style={styles.mzSummaryCard}>
              <span style={styles.metricLabel}>Premio estimado</span>
              <strong style={styles.metricValue}>
                {formatUnits(mzKioskSnapshot.expectedRewardUnits)} MZ
              </strong>
              <span style={styles.metricDetail}>
                Se acredita el mes siguiente y se consume primero.
              </span>
            </article>
          </div>

          {mzWalletError ? (
            <div style={{ ...styles.notice, ...styles.noticeWarning }}>
              {mzWalletError}
            </div>
          ) : null}

          <div style={styles.mzRuleStrip}>
            <div>
              <strong>Regla kiosk vigente</strong>
              <span>
                {MZ_KIOSK_UNITS_PER_1000} MZ cada $1.000 vendidos, solo sin Mercado Pago vinculado.
              </span>
            </div>
            <div>
              <strong>Comisiones y MZ separados</strong>
              <span>
                Con Mercado Pago vinculado no se consumen MZ. Los MZ no descuentan
                ni forman parte del tope de comisiones.
              </span>
            </div>
          </div>

          <div style={styles.mzContentGrid}>
            <div style={styles.mzSectionBlock}>
              <div style={styles.mzSectionHeader}>
                <h3 style={styles.mzSectionTitle}>Packs disponibles</h3>
                <span style={styles.metricDetail}>
                  {mzPackagesLoading
                    ? "Sincronizando catalogo..."
                    : "El boton genera un QR de pago con Mercado Pago y acredita el saldo cuando el cobro queda confirmado."}
                </span>
              </div>

              {mzPackagesError ? (
                <div style={{ ...styles.notice, ...styles.noticeWarning }}>
                  {mzPackagesError}
                </div>
              ) : null}

              <div style={styles.mzPackageGrid}>
                {mzPackages.map((pack) => {
                  const packageCode = pack.packageCode || pack.id;
                  const isPreparingPackageCheckout =
                    selectedMzPackageCode === packageCode;
                  const packUnits = pack.unitsAmount ?? pack.units ?? 0;
                  const packPrice = pack.priceArs ?? pack.price ?? 0;
                  const coveredSalesAmount =
                    (packUnits / MZ_KIOSK_UNITS_PER_1000) * 1000;
                  const effectiveRate =
                    coveredSalesAmount > 0
                      ? (packPrice / coveredSalesAmount) * 100
                      : 0;

                  return (
                    <article key={pack.id} style={styles.mzPackageCard}>
                      <div style={styles.mzPackageHeader}>
                        <strong style={styles.mzPackageName}>{pack.name}</strong>
                        <span style={styles.mzPackagePrice}>
                          {formatCurrency(packPrice)}
                        </span>
                      </div>
                      <strong style={styles.mzPackageUnits}>
                        {formatUnits(packUnits)} MZ
                      </strong>
                      <span style={styles.metricDetail}>
                        Cubre aprox. {formatCurrency(coveredSalesAmount)} vendidos por
                        kiosk.
                      </span>
                      <span style={styles.mzMiniPill}>
                        Costo estimado {formatPercent(effectiveRate)}%
                      </span>
                      <button
                        type="button"
                        style={{
                          ...styles.primaryButton,
                          ...styles.mzPackageBuyButton,
                        }}
                        onClick={() => handleMzPackageAction(pack)}
                        disabled={
                          !canManagePayments ||
                          Boolean(selectedMzPackageCode)
                        }
                      >
                        {isPreparingPackageCheckout
                          ? "Generando QR..."
                          : canManagePayments
                            ? "Comprar"
                            : "Sin permiso de compra"}
                      </button>
                    </article>
                  );
                })}
              </div>

              {selectedMzPackageCode ||
              hasMzCheckoutVisual(mzPackageCheckout) ||
              mzPackageCheckoutError ? (
                <div ref={mzCheckoutPanelRef} style={styles.mzCheckoutPanel}>
                  <div style={styles.mzCheckoutHeader}>
                    <div>
                      <span style={styles.metricLabel}>
                        {selectedMzPackageCode && !hasMzCheckoutVisual(mzPackageCheckout)
                          ? "Generando QR de pago"
                          : mzPackageCheckoutError
                            ? "No pudimos generar el QR"
                            : mzPackageCheckout?.status === "approved"
                              ? "Pago confirmado"
                              : "QR interoperable generado"}
                      </span>
                      <strong style={styles.mzCheckoutTitle}>
                        {mzPackageCheckout?.packageName ||
                          mzPackageCheckoutPendingPack?.name ||
                          "Pack MZ"}
                      </strong>
                    </div>
                    <span style={styles.mzPackagePrice}>
                      {formatCurrency(
                        mzPackageCheckout?.priceArs ||
                          mzPackageCheckoutPendingPack?.priceArs ||
                          mzPackageCheckoutPendingPack?.price ||
                          0
                      )}
                    </span>
                  </div>

                  {selectedMzPackageCode && !hasMzCheckoutVisual(mzPackageCheckout) ? (
                    <div style={styles.mzCheckoutStatusCard}>
                      <strong style={styles.mzPackageUnits}>Generando QR...</strong>
                      <span style={styles.metricDetail}>
                        Estamos creando un QR de pago unico en la cuenta de Mercado
                        Pago de MozzoApp para que puedas escanearlo desde una
                        billetera compatible.
                      </span>
                    </div>
                  ) : mzPackageCheckoutError ? (
                    <div style={styles.mzCheckoutStatusCard}>
                      <strong style={styles.mzPackageUnits}>No se genero el QR</strong>
                      <span style={styles.metricDetail}>
                        {mzPackageCheckoutError}
                      </span>
                      <div style={styles.mzCheckoutActions}>
                        <button
                          type="button"
                          style={styles.primaryButton}
                          onClick={() => {
                            if (mzPackageCheckoutPendingPack) {
                              handleMzPackageAction(mzPackageCheckoutPendingPack);
                            }
                          }}
                          disabled={!mzPackageCheckoutPendingPack}
                        >
                          Reintentar
                        </button>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => {
                            setMzPackageCheckout(null);
                            setMzPackageCheckoutError("");
                            setMzPackageCheckoutPendingPack(null);
                          }}
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  ) : hasMzCheckoutVisual(mzPackageCheckout) ? (
                    <div style={styles.mzCheckoutBody}>
                      <div style={styles.mzQrBox}>
                        {mzPackageCheckout?.qrData ? (
                          <QRCode
                            value={mzPackageCheckout.qrData}
                            size={210}
                            level="M"
                          />
                        ) : mzPackageCheckout?.imageUrl ? (
                          <img
                            src={mzPackageCheckout.imageUrl}
                            alt="QR interoperable para comprar creditos MZ"
                            style={styles.mzQrPreviewImage}
                          />
                        ) : mzPackageCheckout?.checkoutUrl ? (
                          <QRCode
                            value={mzPackageCheckout.checkoutUrl}
                            size={210}
                            level="M"
                          />
                        ) : null}
                      </div>
                      <div style={styles.mzCheckoutCopy}>
                        <strong style={styles.mzPackageUnits}>
                          {formatUnits(mzPackageCheckout.unitsAmount)} MZ
                        </strong>
                        <span style={styles.metricDetail}>
                          Escanea este QR interoperable con Mercado Pago o una
                          billetera compatible para pagar el pack a MozzoApp. La
                          billetera MZ de la empresa se acredita cuando Mercado Pago
                          confirme el cobro.
                        </span>
                        {mzPackageCheckout?.status === "approved" ? (
                          <span style={styles.metricDetail}>
                            Cobro aprobado. Cerramos este panel en unos segundos.
                          </span>
                        ) : null}
                        <div style={styles.mzCheckoutActions}>
                          {mzPackageCheckout?.copyCode ? (
                            <button
                              type="button"
                              style={styles.secondaryButton}
                              onClick={async () => {
                                if (
                                  typeof navigator === "undefined" ||
                                  !navigator.clipboard?.writeText
                                ) {
                                  registerMessage(
                                    "error",
                                    "Este navegador no permite copiar el codigo QR automaticamente."
                                  );
                                  return;
                                }

                                try {
                                  await navigator.clipboard.writeText(
                                    mzPackageCheckout.copyCode
                                  );
                                  registerMessage(
                                    "success",
                                    "Copiamos el codigo del QR interoperable."
                                  );
                                } catch (error) {
                                  registerMessage(
                                    "error",
                                    error?.message ||
                                      "No pudimos copiar el codigo del QR."
                                  );
                                }
                              }}
                            >
                              Copiar codigo QR
                            </button>
                          ) : null}
                          {mzPackageCheckout?.checkoutUrl ? (
                            <button
                              type="button"
                              style={styles.secondaryButton}
                              onClick={() => {
                                if (typeof window !== "undefined") {
                                  window.open(
                                    mzPackageCheckout.checkoutUrl,
                                    "_blank",
                                    "noopener"
                                  );
                                }
                              }}
                            >
                              Abrir link alternativo
                            </button>
                          ) : null}
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => {
                              setMzPackageCheckout(null);
                              setMzPackageCheckoutError("");
                              setMzPackageCheckoutPendingPack(null);
                            }}
                          >
                            Ocultar QR
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={styles.mzSectionBlock}>
              <div style={styles.mzSectionHeader}>
                <h3 style={styles.mzSectionTitle}>Premios del mes</h3>
                <span style={styles.metricDetail}>
                  El premio de volumen no es acumulativo. El de uso digital si puede
                  sumarse.
                </span>
              </div>

              <div style={styles.mzRewardList}>
                <article style={styles.mzRewardCard}>
                  <span style={styles.metricLabel}>Uso digital kiosk</span>
                  <strong style={styles.mzRewardValue}>
                    {formatPercent(mzKioskSnapshot.digitalUsagePercent)}%
                  </strong>
                  <span style={styles.metricDetail}>
                    {mzKioskSnapshot.digitalRewardUnits > 0
                      ? `Objetivo cumplido: +${formatUnits(
                          mzKioskSnapshot.digitalRewardUnits
                        )} MZ.`
                      : mzKioskSnapshot.totalOperationsCount > 0
                        ? `Faltan ${mzKioskSnapshot.digitalOrdersMissing} pedido${
                            mzKioskSnapshot.digitalOrdersMissing === 1 ? "" : "s"
                          } digital${
                            mzKioskSnapshot.digitalOrdersMissing === 1 ? "" : "es"
                          } para llegar al ${MZ_DIGITAL_USAGE_TARGET}%.`
                        : "Todavia no hay pedidos kiosk cobrados este mes."}
                  </span>
                </article>

                <article style={styles.mzRewardCard}>
                  <span style={styles.metricLabel}>Volumen kiosk</span>
                  <strong style={styles.mzRewardValue}>
                    {formatUnits(mzKioskSnapshot.volumeRewardUnits)} MZ
                  </strong>
                  <span style={styles.metricDetail}>
                    {mzKioskSnapshot.volumeRewardUnits > 0
                      ? "Premio de mayor escala alcanzada para el mes siguiente."
                      : mzKioskSnapshot.nextVolumeTier
                        ? `Faltan ${formatCurrency(
                            mzKioskSnapshot.nextVolumeTier.minSalesAmount -
                              mzKioskSnapshot.kioskSalesAmount
                          )} para el primer premio de volumen.`
                        : "Ya alcanzaste la mayor escala de volumen."}
                  </span>
                </article>

                <article style={styles.mzRewardCard}>
                  <span style={styles.metricLabel}>Uso del mes siguiente</span>
                  <strong style={styles.mzRewardValue}>
                    {formatUnits(mzKioskSnapshot.expectedRewardUnits)} MZ
                  </strong>
                  <span style={styles.metricDetail}>
                    Estos MZ se acreditan el mes siguiente y se consumen antes de nuevas
                    comisiones cash.
                  </span>
                </article>
              </div>
            </div>
          </div>
        </section>
      )}

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Caja y arqueo</h2>
            <p style={styles.panelText}>
              Abre la caja con un fondo inicial, registra ingresos o egresos manuales y
              compara el esperado contra el contado real antes de cerrar el turno.
            </p>
          </div>

          {activeCashSession ? (
            <div style={styles.headerActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => {
                  setShowCashClosingForm(false);
                  setShowCashMovementForm((prev) => !prev);
                }}
                disabled={!canOperateCashRegister}
              >
                {showCashMovementForm ? "Ocultar movimiento" : "Registrar movimiento"}
              </button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => {
                  setShowCashMovementForm(false);
                  setShowCashClosingForm((prev) => !prev);
                }}
                disabled={!canOperateCashRegister}
              >
                {showCashClosingForm ? "Ocultar cierre" : "Cerrar caja"}
              </button>
            </div>
          ) : null}
        </div>

        {uncoveredCashCount > 0 && (
          <div style={{ ...styles.notice, ...styles.noticeWarning }}>
            Hay {uncoveredCashCount} cobro{uncoveredCashCount === 1 ? "" : "s"} en
            efectivo registrado{uncoveredCashCount === 1 ? "" : "s"} fuera de una caja
            abierta por {formatCurrency(cashSnapshot.uncoveredCashAmount)}.
          </div>
        )}

        {!activeCashSession ? (
          <>
            <form onSubmit={openCashSession} style={styles.formGrid}>
              <label style={styles.field}>
                <span style={styles.label}>Fondo inicial</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={cashOpeningForm.openingAmount}
                  onChange={(event) =>
                    setCashOpeningForm((prev) => ({
                      ...prev,
                      openingAmount: event.target.value,
                    }))
                  }
                  style={styles.input}
                  placeholder="0"
                  disabled={!canOperateCashRegister}
                />
              </label>

              <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                <span style={styles.label}>Observacion de apertura</span>
                <textarea
                  value={cashOpeningForm.note}
                  onChange={(event) =>
                    setCashOpeningForm((prev) => ({
                      ...prev,
                      note: event.target.value,
                    }))
                  }
                  style={{ ...styles.textarea, minHeight: "96px" }}
                  placeholder="Ej.: caja recibida del turno anterior, cambio disponible, etc."
                  disabled={!canOperateCashRegister}
                />
              </label>

              <div style={styles.formActions}>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={!canOperateCashRegister}
                >
                  Abrir caja
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <div style={styles.cashSummaryGrid}>
              <article style={styles.cashSummaryCard}>
                <span style={styles.metricLabel}>Apertura</span>
                <strong style={styles.metricValue}>
                  {formatCurrency(activeCashSession.openingAmount)}
                </strong>
                <span style={styles.metricDetail}>
                  {formatDateTime(activeCashSession.openedAt)} · {activeCashSession.openedBy || "Caja"}
                </span>
                {activeCashSession.openingNote ? (
                  <span style={styles.metricDetail}>{activeCashSession.openingNote}</span>
                ) : null}
              </article>

              <article style={styles.cashSummaryCard}>
                <span style={styles.metricLabel}>Efectivo cobrado</span>
                <strong style={styles.metricValue}>
                  {formatCurrency(activeCashSession.cashSalesAmount)}
                </strong>
                <span style={styles.metricDetail}>
                  {activeCashSession.cashSalesCount} cobro
                  {activeCashSession.cashSalesCount === 1 ? "" : "s"} contado
                </span>
              </article>

              <article style={styles.cashSummaryCard}>
                <span style={styles.metricLabel}>Movimientos manuales</span>
                <strong style={styles.metricValue}>
                  {formatCurrency(activeCashSession.manualNetAmount)}
                </strong>
                <span style={styles.metricDetail}>
                  Ingresos {formatCurrency(activeCashSession.manualIncomeAmount)} · Egresos{" "}
                  {formatCurrency(activeCashSession.manualExpenseAmount)}
                </span>
              </article>

              <article style={styles.cashSummaryCard}>
                <span style={styles.metricLabel}>Esperado en caja</span>
                <strong style={styles.metricValue}>
                  {formatCurrency(activeCashSession.expectedAmount)}
                </strong>
                <span style={styles.metricDetail}>
                  Fondo inicial + efectivo + movimientos del turno
                </span>
              </article>
            </div>

            {showCashMovementForm && (
              <form onSubmit={addCashMovement} style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Tipo</span>
                  <select
                    value={cashMovementForm.kind}
                    onChange={(event) =>
                      setCashMovementForm((prev) => ({
                        ...prev,
                        kind: event.target.value,
                        reason: "",
                      }))
                    }
                    style={styles.input}
                    disabled={!canOperateCashRegister}
                  >
                    {Object.entries(CASH_MOVEMENT_KIND_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={styles.field}>
                  <span style={styles.label}>Importe</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={cashMovementForm.amount}
                    onChange={(event) =>
                      setCashMovementForm((prev) => ({
                        ...prev,
                        amount: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder="0"
                    disabled={!canOperateCashRegister}
                  />
                </label>

                <label style={styles.field}>
                  <span style={styles.label}>Motivo</span>
                  <input
                    type="text"
                    value={cashMovementForm.reason}
                    onChange={(event) =>
                      setCashMovementForm((prev) => ({
                        ...prev,
                        reason: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder={
                      cashMovementForm.kind === "income"
                        ? "Cambio, reposicion, ajuste positivo..."
                        : "Retiro, gasto menor, pago a proveedor..."
                    }
                    disabled={!canOperateCashRegister}
                  />
                  <div style={styles.presetChipRow}>
                    {cashMovementReasonPresets.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        style={{
                          ...styles.presetChip,
                          ...(cashMovementForm.reason === preset
                            ? styles.presetChipActive
                            : {}),
                        }}
                        onClick={() =>
                          setCashMovementForm((prev) => ({
                            ...prev,
                            reason: preset,
                          }))
                        }
                        disabled={!canOperateCashRegister}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </label>

                <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                  <span style={styles.label}>Nota interna</span>
                  <textarea
                    value={cashMovementForm.note}
                    onChange={(event) =>
                      setCashMovementForm((prev) => ({
                        ...prev,
                        note: event.target.value,
                      }))
                    }
                    style={{ ...styles.textarea, minHeight: "96px" }}
                    placeholder="Detalle rapido del movimiento."
                    disabled={!canOperateCashRegister}
                  />
                </label>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => {
                      setCashMovementForm(initialCashMovementForm);
                      setShowCashMovementForm(false);
                    }}
                  >
                    Cancelar
                  </button>
                  <button type="submit" style={styles.primaryButton} disabled={!canOperateCashRegister}>
                    Guardar movimiento
                  </button>
                </div>
              </form>
            )}

            {showCashClosingForm && (
              <form onSubmit={closeCashSession} style={styles.formGrid}>
                {pendingCashOperationsForClosing.length > 0 && (
                  <div
                    style={{
                      ...styles.notice,
                      ...styles.noticeWarning,
                      gridColumn: "1 / -1",
                    }}
                  >
                    Antes de cerrar, resuelve {pendingCashOperationsForClosing.length} cobro
                    {pendingCashOperationsForClosing.length === 1 ? "" : "s"} en
                    efectivo pendiente{pendingCashOperationsForClosing.length === 1 ? "" : "s"}:
                    {" "}
                    {pendingCashOperationsForClosing
                      .slice(0, 3)
                      .map((operation) => operation.mesa || operation.canal || "Operacion")
                      .join(", ")}
                    {pendingCashOperationsForClosing.length > 3 ? ", ..." : ""}.
                  </div>
                )}

                <label style={styles.field}>
                  <span style={styles.label}>Contado real</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={cashClosingForm.countedAmount}
                    onChange={(event) =>
                      setCashClosingForm((prev) => ({
                        ...prev,
                        countedAmount: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder="0"
                    disabled={!canOperateCashRegister}
                  />
                </label>

                <div style={styles.cashClosingPreview}>
                  <span style={styles.label}>Diferencia estimada</span>
                  <strong
                    style={{
                      ...styles.cashDifferenceValue,
                      color:
                        closingDifferencePreview === null
                          ? "#0f172a"
                          : closingDifferencePreview === 0
                            ? "#166534"
                            : closingDifferencePreview > 0
                              ? "#1d4ed8"
                              : "#b91c1c",
                    }}
                  >
                    {closingDifferencePreview === null
                      ? "Ingresa el contado para ver la diferencia"
                      : formatCurrency(closingDifferencePreview)}
                  </strong>
                  <span style={styles.metricDetail}>
                    Esperado actual: {formatCurrency(activeCashSession.expectedAmount)}
                  </span>
                </div>

                <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
                  <span style={styles.label}>Observacion de cierre</span>
                  <textarea
                    value={cashClosingForm.note}
                    onChange={(event) =>
                      setCashClosingForm((prev) => ({
                        ...prev,
                        note: event.target.value,
                      }))
                    }
                    style={{ ...styles.textarea, minHeight: "96px" }}
                    placeholder="Faltantes, sobrantes, retiro final, entrega al siguiente turno..."
                    disabled={!canOperateCashRegister}
                  />
                </label>

                <label style={{ ...styles.checkboxField, gridColumn: "1 / -1" }}>
                  <input
                    type="checkbox"
                    checked={cashClosingForm.confirmReviewed}
                    onChange={(event) =>
                      setCashClosingForm((prev) => ({
                        ...prev,
                        confirmReviewed: event.target.checked,
                      }))
                    }
                    style={styles.checkboxInput}
                    disabled={!canOperateCashRegister}
                  />
                  <span style={styles.checkboxText}>
                    Confirmo que conte el efectivo, revise los movimientos del turno y estoy
                    listo para cerrar esta caja.
                  </span>
                </label>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => {
                      setCashClosingForm(initialCashClosingForm);
                      setShowCashClosingForm(false);
                    }}
                  >
                    Cancelar
                  </button>
                  <button type="submit" style={styles.primaryButton} disabled={!canOperateCashRegister}>
                    Confirmar cierre
                  </button>
                </div>
              </form>
            )}

            <div style={styles.cashColumns}>
              <div style={styles.cashColumn}>
                <div style={styles.cashColumnHeader}>
                  <strong style={styles.methodTitle}>Movimientos del turno</strong>
                  <span style={styles.methodHint}>
                    Ingresos y egresos manuales que impactan la caja abierta.
                  </span>
                </div>

                <div style={styles.cashMovementList}>
                  {activeCashSession.movements.length === 0 ? (
                    <div style={styles.emptyState}>
                      Todavia no hay movimientos manuales registrados en esta caja.
                    </div>
                  ) : (
                    [...activeCashSession.movements]
                      .sort(
                        (left, right) =>
                          new Date(right.createdAt || 0).getTime() -
                          new Date(left.createdAt || 0).getTime()
                      )
                      .map((movement) => (
                        <div key={movement.id} style={styles.cashMovementItem}>
                          <div>
                            <strong style={styles.operationTitle}>
                              {CASH_MOVEMENT_KIND_LABELS[movement.kind]}
                            </strong>
                            <div style={styles.operationMeta}>
                              {movement.reason || "Movimiento manual"} ·{" "}
                              {formatDateTime(movement.createdAt)} ·{" "}
                              {movement.createdBy || "Caja"}
                            </div>
                            {movement.note ? (
                              <div style={styles.operationSummary}>{movement.note}</div>
                            ) : null}
                          </div>
                          <strong
                            style={{
                              color:
                                movement.kind === "income" ? "#166534" : "#b91c1c",
                            }}
                          >
                            {movement.kind === "income" ? "+ " : "- "}
                            {formatCurrency(movement.amount)}
                          </strong>
                        </div>
                      ))
                  )}
                </div>
              </div>

              <div style={styles.cashColumn}>
                <div style={styles.cashColumnHeader}>
                  <strong style={styles.methodTitle}>Ultimos arqueos cerrados</strong>
                  <span style={styles.methodHint}>
                    Historial corto para revisar diferencias del turno.
                  </span>
                  <div style={styles.cashColumnActions}>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={exportCashRegisterHistory}
                      disabled={!canManagePayments}
                    >
                      Exportar CSV
                    </button>
                  </div>
                </div>

                <div style={styles.cashMovementList}>
                  {cashSnapshot.recentClosedSessions.length === 0 ? (
                    <div style={styles.emptyState}>
                      Todavia no hay cierres de caja registrados en esta sucursal.
                    </div>
                  ) : (
                    cashSnapshot.recentClosedSessions.map((session) => (
                      <div key={session.id} style={styles.cashSessionItem}>
                        <div>
                          <strong style={styles.operationTitle}>
                            {formatDateTime(session.closedAt)}
                          </strong>
                          <div style={styles.operationMeta}>
                            Esperado {formatCurrency(session.expectedAmount)} · Contado{" "}
                            {formatCurrency(session.countedClosingAmount)} · Cierra{" "}
                            {session.closedBy || "Caja"}
                          </div>
                          {session.closingNote ? (
                            <div style={styles.operationSummary}>{session.closingNote}</div>
                          ) : null}
                        </div>
                        <strong
                          style={{
                            color:
                              Number(session.differenceAmount || 0) === 0
                                ? "#166534"
                                : Number(session.differenceAmount || 0) > 0
                                  ? "#1d4ed8"
                                  : "#b91c1c",
                          }}
                        >
                          {formatCurrency(session.differenceAmount || 0)}
                        </strong>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {showManualForm && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Nueva operacion manual</h2>
              <p style={styles.panelText}>
                Util para mesas o pedidos que no quisieron interactuar con la app y se
                cargaron directamente desde salon o caja.
              </p>
            </div>
          </div>

          <form onSubmit={handleCreateManualOperation} style={styles.formGrid}>
            <label style={styles.field}>
              <span style={styles.label}>Mesa o referencia</span>
              <input
                type="text"
                value={manualForm.mesa}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, mesa: event.target.value }))
                }
                style={styles.input}
                placeholder="Mesa 14, Delivery 2, Retiro..."
                disabled={!canChargeOrders}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Canal</span>
              <select
                value={manualForm.canal}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, canal: event.target.value }))
                }
                style={styles.input}
                disabled={!canChargeOrders}
              >
                <option value="Salon">Salon</option>
                <option value="Barra">Barra</option>
                <option value="Retiro">Retiro</option>
                <option value="Delivery">Delivery</option>
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Monto</span>
              <input
                type="number"
                min="0"
                step="1"
                value={manualForm.amount}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                style={styles.input}
                placeholder="0"
                disabled={!canChargeOrders}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Medio de pago previsto</span>
              <select
                value={manualForm.method}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, method: event.target.value }))
                }
                style={styles.input}
                disabled={!canChargeOrders}
              >
                {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
              <span style={styles.label}>Detalle del pedido</span>
              <textarea
                value={manualForm.detail}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, detail: event.target.value }))
                }
                style={styles.textarea}
                placeholder="Que se pidio, observaciones o referencia interna..."
                disabled={!canChargeOrders}
              />
            </label>

            <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
              <span style={styles.label}>Notas internas</span>
              <textarea
                value={manualForm.notes}
                onChange={(event) =>
                  setManualForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                style={{ ...styles.textarea, minHeight: "96px" }}
                placeholder="Ej.: quiere pagar con dos tarjetas, espera factura, etc."
                disabled={!canChargeOrders}
              />
            </label>

            <div style={styles.formActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => {
                  setManualForm(initialManualForm);
                  setShowManualForm(false);
                }}
              >
                Cancelar
              </button>
              <button type="submit" style={styles.primaryButton} disabled={!canChargeOrders}>
                Guardar operacion
              </button>
            </div>
          </form>
        </section>
      )}

      <section style={styles.metricGrid}>
        {metrics.map((metric) => (
          <article key={metric.id} style={styles.metricCard}>
            <span style={styles.metricLabel}>{metric.label}</span>
            <strong style={styles.metricValue}>{metric.value}</strong>
            <span style={styles.metricDetail}>{metric.detail}</span>
          </article>
        ))}
      </section>

      <section style={styles.contentGrid}>
        <article style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
                  <h2 style={styles.panelTitle}>Operaciones del turno</h2>
                  <p style={styles.panelText}>
                    Puedes filtrar pendientes, cobradas o cerradas. Aqui seguimos los cobros
                    digitales del menu clasico y del kiosk; los cobros en caja del totem se
                    resuelven desde Inicio > Notificaciones.
                  </p>
                </div>
              </div>

          <div style={styles.toolbar}>
            <div style={styles.filterRow}>
              {FILTER_OPTIONS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setActiveFilter(filter.id)}
                  style={{
                    ...styles.filterButton,
                    ...(activeFilter === filter.id ? styles.filterButtonActive : {}),
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Buscar por mesa, canal o detalle"
              style={styles.searchInput}
            />
          </div>

          <div style={styles.operationList}>
            {filteredOperations.length === 0 ? (
              <div style={styles.emptyState}>
                No hay operaciones para este filtro todavia.
              </div>
            ) : (
              filteredOperations.map((operation) => {
                const requestedLabel = operation.requestedMethod
                  ? PAYMENT_METHOD_LABELS[operation.requestedMethod]
                  : null;

                return (
                  <button
                    key={operation.id}
                    type="button"
                    onClick={() => setSelectedOperationId(operation.id)}
                    style={{
                      ...styles.operationCard,
                      ...(selectedOperation?.id === operation.id
                        ? styles.operationCardActive
                        : {}),
                    }}
                  >
                    <div style={styles.operationHeader}>
                      <div>
                        <strong style={styles.operationTitle}>{operation.mesa}</strong>
                        <div style={styles.operationMeta}>
                          {operation.canal} - {SOURCE_LABELS[operation.source]}
                        </div>
                      </div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...STATUS_STYLES[operation.status],
                        }}
                      >
                        {STATUS_LABELS[operation.status]}
                      </span>
                    </div>

                    <div style={styles.operationAmount}>
                      {formatCurrency(getGrossAmount(operation))}
                    </div>

                    <div style={styles.operationSummary}>{operation.itemsSummary}</div>

                    <div style={styles.operationFooter}>
                      <span>{formatElapsed(operation.orderedAt)}</span>
                      <span>
                        {requestedLabel
                          ? `Elegido en app: ${requestedLabel}`
                          : `Medio final: ${PAYMENT_METHOD_LABELS[operation.selectedMethod]}`}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </article>

        <article style={styles.panel}>
          {!selectedOperation ? (
            <div style={styles.emptyState}>
              Selecciona una operacion para revisar su cobro, registrar el medio final y
              cerrarla.
            </div>
          ) : (
            <>
              <div style={styles.panelHeader}>
                <div>
                  <h2 style={styles.panelTitle}>{selectedOperation.mesa}</h2>
                  <p style={styles.panelText}>
                    {selectedOperation.canal} - {SOURCE_LABELS[selectedOperation.source]}
                  </p>
                </div>
                <span
                  style={{
                    ...styles.statusBadge,
                    ...STATUS_STYLES[selectedOperation.status],
                  }}
                >
                  {STATUS_LABELS[selectedOperation.status]}
                </span>
              </div>

              <div style={styles.detailGrid}>
                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Venta bruta</span>
                  <strong style={styles.detailValue}>
                    {formatCurrency(getGrossAmount(selectedOperation))}
                  </strong>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>
                    Comision plataforma ({formatPercent(getCommissionRate(selectedOperation) * 100)}%)
                  </span>
                  <strong style={styles.detailValue}>
                    {formatCurrency(getPlatformFeeAmount(selectedOperation))}
                  </strong>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Neto del ticket</span>
                  <strong style={styles.detailValue}>
                    {formatCurrency(getMerchantNetAmount(selectedOperation))}
                  </strong>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Retenido en esta liquidacion</span>
                  <div style={styles.detailText}>
                    {selectedOperation.status === "paid" || selectedOperation.status === "closed"
                      ? formatCurrency(getRetainedAmount(selectedOperation))
                      : "Se calcula al registrar el cobro"}
                  </div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Deuda pendiente del ticket</span>
                  <div style={styles.detailText}>
                    {selectedOperation.status === "paid" || selectedOperation.status === "closed"
                      ? formatCurrency(getPendingDebtAmount(selectedOperation))
                      : "Todavia no generada"}
                  </div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Movimiento al local</span>
                  <div style={styles.detailText}>
                    {selectedOperation.status === "paid" || selectedOperation.status === "closed"
                      ? selectedOperation.selectedMethod === "deep_link"
                        ? `Transferido: ${formatCurrency(getPayoutAmount(selectedOperation))}`
                        : `Cobrado por el local: ${formatCurrency(getPayoutAmount(selectedOperation))}`
                      : "Pendiente de registrar"}
                  </div>
                </div>

                {selectedOperation.pricing ? (
                  <div style={styles.detailBlock}>
                    <span style={styles.detailLabel}>Desglose comercial</span>
                    <div style={styles.pricingBreakdown}>
                      <div style={styles.pricingRow}>
                        <span>Precio de lista</span>
                        <strong>{formatCurrency(selectedOperation.pricing.listTotal)}</strong>
                      </div>
                      <div style={styles.pricingRow}>
                        <span>Descuento aplicado</span>
                        <strong>
                          {selectedOperation.pricing.totalDiscount > 0
                            ? `- ${formatCurrency(selectedOperation.pricing.totalDiscount)}`
                            : "Sin descuento"}
                        </strong>
                      </div>
                      {Array.isArray(selectedOperation.pricing.adjustments) &&
                      selectedOperation.pricing.adjustments.length > 0 ? (
                        <div style={styles.pricingAdjustmentList}>
                          {selectedOperation.pricing.adjustments.map((adjustment) => (
                            <div
                              key={`${selectedOperation.id}-${adjustment.id}`}
                              style={styles.pricingAdjustmentItem}
                            >
                              <span>
                                {adjustment.title ||
                                  adjustment.summary ||
                                  "Ajuste comercial"}
                              </span>
                              <strong>{`- ${formatCurrency(adjustment.amount)}`}</strong>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Pedido</span>
                  <div style={styles.detailText}>{selectedOperation.itemsSummary}</div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Pedido cargado</span>
                  <div style={styles.detailText}>
                    {formatDateTime(selectedOperation.orderedAt)}
                  </div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Cobrado</span>
                  <div style={styles.detailText}>
                    {selectedOperation.paidAt
                      ? formatDateTime(selectedOperation.paidAt)
                      : "Todavia no registrado"}
                  </div>
                </div>

                <div style={styles.detailBlock}>
                  <span style={styles.detailLabel}>Origen del medio</span>
                  <div style={styles.detailText}>
                    {selectedOperationManagedFromInicio
                      ? "El cliente eligio pagar en caja desde el totem. Caja lo confirma en Inicio > Notificaciones cuando el pedido pasa a preparacion, y aqui solo queda el seguimiento de comision."
                      : selectedOperation.requestedMethod
                        ? `El comensal eligio ${PAYMENT_METHOD_LABELS[selectedOperation.requestedMethod]} desde la app.`
                      : "El pedido fue manual, asi que el medio lo define el equipo en caja o salon."}
                  </div>
                </div>

                {Array.isArray(getEconomics(selectedOperation)?.settlementSources) &&
                getEconomics(selectedOperation)?.settlementSources.length > 0 ? (
                  <div style={styles.detailBlock}>
                    <span style={styles.detailLabel}>Deuda vieja descontada aca</span>
                    <div style={styles.pricingAdjustmentList}>
                      {getEconomics(selectedOperation).settlementSources.map((entry) => (
                        <div
                          key={`${selectedOperation.id}-source-${entry.operationId}-${entry.amount}`}
                          style={styles.pricingAdjustmentItem}
                        >
                          <span>{entry.mesa || entry.orderId || "Ticket anterior"}</span>
                          <strong>{formatCurrency(entry.amount)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {Array.isArray(getEconomics(selectedOperation)?.settlementTargets) &&
                getEconomics(selectedOperation)?.settlementTargets.length > 0 ? (
                  <div style={styles.detailBlock}>
                    <span style={styles.detailLabel}>Compensado por cobros digitales posteriores</span>
                    <div style={styles.pricingAdjustmentList}>
                      {getEconomics(selectedOperation).settlementTargets.map((entry) => (
                        <div
                          key={`${selectedOperation.id}-target-${entry.operationId}-${entry.amount}`}
                          style={styles.pricingAdjustmentItem}
                        >
                          <span>{entry.mesa || entry.orderId || "Cobro digital"}</span>
                          <strong>{formatCurrency(entry.amount)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {selectedOperationManagedFromInicio ? (
                <div style={styles.methodPanel}>
                  <div style={styles.methodHeader}>
                    <strong style={styles.methodTitle}>Cobro administrado desde Inicio</strong>
                    <span style={styles.methodHint}>
                      Cuando el pedido del totem se cobra en caja desde Inicio > Notificaciones
                      y pasa a preparacion, la operacion se cierra sola y aqui solo queda el
                      registro para comisiones.
                    </span>
                  </div>
                </div>
              ) : (
                <div style={styles.methodPanel}>
                  <div style={styles.methodHeader}>
                    <strong style={styles.methodTitle}>Medio final de cobro</strong>
                    <span style={styles.methodHint}>
                      Puedes ajustarlo si el comensal cambio la forma de pago al final.
                    </span>
                  </div>

                  <div style={styles.methodSelector}>
                    {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setOperationMethod(selectedOperation.id, value)}
                        disabled={
                          selectedOperation.status === "closed" ||
                          !canChargeOrders ||
                          selectedOperationAlreadyClaimed
                        }
                        style={{
                          ...styles.methodButton,
                          ...(selectedOperation.selectedMethod === value
                            ? styles.methodButtonActive
                            : {}),
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label style={styles.notesField}>
                <span style={styles.detailLabel}>Notas de caja</span>
                <textarea
                  value={selectedOperation.notes || ""}
                  onChange={(event) =>
                    updateOperationNotes(selectedOperation.id, event.target.value)
                  }
                  style={styles.textarea}
                  placeholder="Observaciones del cobro, comprobantes, cambios de medio, etc."
                  disabled={!canManagePayments}
                />
              </label>

              <div style={styles.actionRow}>
                {selectedOperationAlreadyClaimed ? (
                  <div style={{ ...styles.notice, ...styles.noticeWarning }}>
                    Este pedido ya fue cobrado desde Caja o por un mozo. El cobro queda
                    bloqueado en esta operacion para evitar duplicados.
                  </div>
                ) : null}
                {selectedOperation.status === "pending" &&
                  selectedOperation.selectedMethod === "deep_link" && (
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={() => sendDeepLink(selectedOperation.id)}
                      disabled={!canChargeOrders || selectedOperationAlreadyClaimed}
                    >
                      Enviar deep link
                    </button>
                  )}

                {selectedOperation.status === "link_sent" && (
                  <>
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={() => markAsPaid(selectedOperation.id)}
                      disabled={!canChargeOrders || selectedOperationAlreadyClaimed}
                    >
                      {selectedOperation.selectedMethod === "pay_by_phone"
                        ? "Confirmar cobro del QR"
                        : "Confirmar cobro del link"}
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => revertToPending(selectedOperation.id)}
                      disabled={!canRefundOrders}
                    >
                      Volver a pendiente
                    </button>
                  </>
                )}

                {selectedOperation.status === "pending" &&
                  !selectedOperationManagedFromInicio &&
                  selectedOperation.selectedMethod !== "deep_link" && (
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={() => markAsPaid(selectedOperation.id)}
                      disabled={!canChargeOrders || selectedOperationAlreadyClaimed}
                    >
                      Registrar cobro en{" "}
                      {PAYMENT_METHOD_LABELS[selectedOperation.selectedMethod]}
                    </button>
                  )}

                {selectedOperation.status === "paid" && (
                  <>
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={() => closeOperation(selectedOperation.id)}
                      disabled={!canChargeOrders}
                    >
                      Cerrar operacion
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => revertToPending(selectedOperation.id)}
                      disabled={!canRefundOrders}
                    >
                      {selectedOperation.paymentIntentId
                        ? "Reintegrar cobro"
                        : "Reabrir cobro"}
                    </button>
                  </>
                )}

                {selectedOperation.status === "closed" && (
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => reopenOperation(selectedOperation.id)}
                    disabled={!canRefundOrders}
                  >
                    Reabrir operacion
                  </button>
                )}
              </div>

              <div style={styles.timelinePanel}>
                <div style={styles.timelineHeader}>
                  <strong style={styles.methodTitle}>Bitacora de la operacion</strong>
                  <span style={styles.methodHint}>
                    Sirve para ver rapido como avanzo el cobro.
                  </span>
                </div>

                <div style={styles.timelineList}>
                  {(selectedOperation.history || []).map((entry) => (
                    <div key={entry.id} style={styles.timelineItem}>
                      <div style={styles.timelineDot} />
                      <div style={styles.timelineContent}>
                        <strong style={styles.timelineLabel}>{entry.label}</strong>
                        <span style={styles.timelineDate}>{formatDateTime(entry.at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </article>
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "22px",
    padding: "8px 6px 20px",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
  },
  headerCopy: {
    display: "grid",
    gap: "10px",
    maxWidth: "860px",
  },
  overline: {
    color: "var(--brand-primary)",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.7,
  },
  branchPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "9px 12px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft)",
    color: "var(--brand-primary)",
    fontWeight: 700,
    fontSize: "13px",
    width: "fit-content",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  mzHeaderButton: {
    border: "1px solid #8fd4ff",
    borderRadius: "14px",
    padding: "10px 14px",
    backgroundColor: "#e7f6ff",
    color: "#0f3f66",
    cursor: "pointer",
    fontWeight: 800,
    display: "grid",
    gap: "2px",
    minWidth: "150px",
    textAlign: "left",
    boxShadow: "0 12px 24px rgba(14, 116, 144, 0.1)",
  },
  mzHeaderButtonActive: {
    borderColor: "#0284c7",
    boxShadow: "0 0 0 3px rgba(2, 132, 199, 0.14)",
  },
  mzButtonWarning: {
    borderColor: "#facc15",
    backgroundColor: "#fef9c3",
    color: "#854d0e",
  },
  mzButtonDanger: {
    borderColor: "#fca5a5",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
  },
  notice: {
    borderRadius: "14px",
    padding: "14px 16px",
    fontWeight: 600,
  },
  noticeSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  noticeWarning: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fcd34d",
    color: "#92400e",
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    padding: "20px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
    display: "grid",
    gap: "18px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  panelTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "20px",
  },
  panelText: {
    marginTop: "8px",
    marginBottom: 0,
    color: "#64748b",
    lineHeight: 1.55,
    maxWidth: "760px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  field: {
    display: "grid",
    gap: "8px",
  },
  label: {
    color: "#334155",
    fontWeight: 700,
    fontSize: "14px",
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  textarea: {
    width: "100%",
    minHeight: "120px",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
  },
  checkboxField: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #dbe6f3",
    backgroundColor: "#f8fbff",
  },
  checkboxInput: {
    marginTop: "2px",
    width: "18px",
    height: "18px",
    accentColor: "var(--brand-primary)",
    cursor: "pointer",
    flexShrink: 0,
  },
  checkboxText: {
    color: "#334155",
    lineHeight: 1.5,
    fontWeight: 600,
  },
  presetChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  presetChip: {
    border: "1px solid #cbd5e1",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    color: "#475569",
    padding: "7px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "12px",
  },
  presetChipActive: {
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    gridColumn: "1 / -1",
  },
  primaryButton: {
    border: "1px solid var(--brand-primary)",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "var(--brand-primary)",
    color: "var(--brand-secondary)",
    cursor: "pointer",
    fontWeight: 700,
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    fontWeight: 700,
  },
  mzSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "14px",
  },
  mzSummaryCard: {
    border: "1px solid #bae6fd",
    borderRadius: "14px",
    padding: "16px",
    backgroundColor: "#f0f9ff",
    display: "grid",
    gap: "8px",
    alignContent: "start",
  },
  mzRuleStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#7c2d12",
  },
  mzContentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 0.75fr)",
    gap: "16px",
    alignItems: "start",
  },
  mzSectionBlock: {
    display: "grid",
    gap: "14px",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  mzSectionHeader: {
    display: "grid",
    gap: "5px",
  },
  mzSectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
  },
  mzPackageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: "12px",
  },
  mzPackageCard: {
    display: "grid",
    gap: "10px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    alignContent: "start",
  },
  mzPackageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
  },
  mzPackageName: {
    color: "#0f172a",
  },
  mzPackagePrice: {
    color: "#0369a1",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  mzPackageUnits: {
    color: "#0f172a",
    fontSize: "24px",
    lineHeight: 1.1,
  },
  mzPackageBuyButton: {
    width: "100%",
    justifySelf: "stretch",
  },
  mzCheckoutPanel: {
    display: "grid",
    gap: "14px",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #0ea5e9",
    backgroundColor: "#f0f9ff",
    boxShadow: "0 16px 34px rgba(2, 132, 199, 0.12)",
  },
  mzCheckoutHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  mzCheckoutTitle: {
    display: "block",
    marginTop: "4px",
    color: "#0f172a",
    fontSize: "20px",
  },
  mzCheckoutBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    alignItems: "center",
  },
  mzQrBox: {
    width: "240px",
    minHeight: "240px",
    borderRadius: "14px",
    border: "1px solid #bae6fd",
    backgroundColor: "#ffffff",
    display: "grid",
    placeItems: "center",
    padding: "14px",
    boxSizing: "border-box",
    justifySelf: "center",
  },
  mzQrPreviewImage: {
    width: "100%",
    maxWidth: "210px",
    height: "auto",
    display: "block",
  },
  mzCheckoutCopy: {
    display: "grid",
    gap: "10px",
    alignContent: "center",
  },
  mzCheckoutStatusCard: {
    display: "grid",
    gap: "10px",
    padding: "18px",
    borderRadius: "14px",
    border: "1px solid #bae6fd",
    backgroundColor: "#ffffff",
  },
  mzCheckoutActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  mzMiniPill: {
    width: "fit-content",
    borderRadius: "999px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    padding: "7px 10px",
    fontSize: "12px",
    fontWeight: 800,
  },
  mzRewardList: {
    display: "grid",
    gap: "12px",
  },
  mzRewardCard: {
    display: "grid",
    gap: "8px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
  },
  mzRewardValue: {
    color: "#0f172a",
    fontSize: "22px",
    lineHeight: 1.15,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
  },
  cashSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
  },
  metricCard: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    padding: "18px",
    border: "1px solid #dbe2ea",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.04)",
    display: "grid",
    gap: "8px",
  },
  cashSummaryCard: {
    backgroundColor: "#f8fbff",
    borderRadius: "14px",
    padding: "18px",
    border: "1px solid #dbe6f3",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.03)",
    display: "grid",
    gap: "8px",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },
  metricValue: {
    fontSize: "28px",
    color: "#0f172a",
  },
  metricDetail: {
    color: "#475569",
    fontSize: "13px",
  },
  cashColumns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "16px",
  },
  cashColumn: {
    display: "grid",
    gap: "12px",
  },
  cashColumnHeader: {
    display: "grid",
    gap: "4px",
  },
  cashColumnActions: {
    display: "flex",
    justifyContent: "flex-start",
    gap: "10px",
    marginTop: "6px",
  },
  cashMovementList: {
    display: "grid",
    gap: "10px",
  },
  cashMovementItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
  },
  cashSessionItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  cashClosingPreview: {
    display: "grid",
    gap: "8px",
    borderRadius: "14px",
    border: "1px solid #dbe6f3",
    backgroundColor: "#f8fbff",
    padding: "14px",
    alignContent: "start",
  },
  cashDifferenceValue: {
    fontSize: "24px",
    lineHeight: 1.2,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
    gap: "18px",
    alignItems: "start",
  },
  toolbar: {
    display: "grid",
    gap: "14px",
  },
  filterRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  filterButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  filterButtonActive: {
    backgroundColor: "#0f172a",
    borderColor: "#0f172a",
    color: "#ffffff",
  },
  searchInput: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  operationList: {
    display: "grid",
    gap: "12px",
  },
  operationCard: {
    width: "100%",
    textAlign: "left",
    border: "1px solid #dbe2ea",
    borderRadius: "14px",
    padding: "16px",
    backgroundColor: "#fbfdff",
    cursor: "pointer",
    display: "grid",
    gap: "12px",
  },
  operationCardActive: {
    borderColor: "#93c5fd",
    boxShadow: "0 0 0 1px rgba(37, 99, 235, 0.14)",
    backgroundColor: "#f8fbff",
  },
  operationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  operationTitle: {
    color: "#0f172a",
  },
  operationMeta: {
    marginTop: "6px",
    color: "#64748b",
    fontSize: "13px",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  operationAmount: {
    color: "#0f172a",
    fontWeight: 800,
    fontSize: "24px",
  },
  operationSummary: {
    color: "#334155",
    lineHeight: 1.55,
  },
  operationFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    color: "#64748b",
    fontSize: "13px",
  },
  emptyState: {
    border: "1px dashed #cbd5e1",
    borderRadius: "16px",
    padding: "22px",
    color: "#64748b",
    backgroundColor: "#ffffff",
  },
  detailGrid: {
    display: "grid",
    gap: "14px",
  },
  detailBlock: {
    display: "grid",
    gap: "6px",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fbfdff",
  },
  detailLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  detailValue: {
    color: "#0f172a",
    fontSize: "26px",
  },
  detailText: {
    color: "#334155",
    lineHeight: 1.55,
  },
  pricingBreakdown: {
    display: "grid",
    gap: "8px",
  },
  pricingRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#334155",
  },
  pricingAdjustmentList: {
    display: "grid",
    gap: "6px",
    marginTop: "4px",
  },
  pricingAdjustmentItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "8px 10px",
    borderRadius: "10px",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    fontSize: "13px",
  },
  methodPanel: {
    display: "grid",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid #dbe6f3",
    backgroundColor: "#f8fbff",
  },
  methodHeader: {
    display: "grid",
    gap: "4px",
  },
  methodTitle: {
    color: "#0f172a",
  },
  methodHint: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  methodSelector: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  methodButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  methodButtonActive: {
    backgroundColor: "#0f172a",
    borderColor: "#0f172a",
    color: "#ffffff",
  },
  notesField: {
    display: "grid",
    gap: "8px",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  timelinePanel: {
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  timelineHeader: {
    display: "grid",
    gap: "4px",
  },
  timelineList: {
    display: "grid",
    gap: "12px",
  },
  timelineItem: {
    display: "grid",
    gridTemplateColumns: "12px minmax(0, 1fr)",
    gap: "12px",
    alignItems: "start",
  },
  timelineDot: {
    width: "12px",
    height: "12px",
    marginTop: "4px",
    borderRadius: "50%",
    backgroundColor: "#2563eb",
    boxShadow: "0 0 0 5px rgba(37,99,235,0.12)",
  },
  timelineContent: {
    display: "grid",
    gap: "4px",
  },
  timelineLabel: {
    color: "#0f172a",
  },
  timelineDate: {
    color: "#64748b",
    fontSize: "13px",
  },
};

export default Pagos;
